// Entry point — wires up the UI shell defined in index.html.
//
// This file holds the small amount of mutable state for the app:
//   - currentFolder         the picked FileSystemDirectoryHandle
//   - bundle                the in-memory dashboard data (read from disk
//                           on folder pick, written back after every op)
//   - cache                 vendor_key → {category, subcategory}, the
//                           "remember this for next time" map
//   - categories            the taxonomy, loaded from categories.json
//                           (seeded if missing)
//
// All disk writes go through state.ts; rendering reads off `bundle`.
// Closing the tab and coming back tomorrow re-reads everything from
// disk into the same structure, so the UI looks identical.

import {
  isFolderPickerSupported,
  pickDataFolder,
  getStoredFolder,
  forgetStoredFolder,
  folderFingerprint,
} from "./folder.js";
import { fileToInput, processFiles, type InputFile } from "./processMonth.js";
import type { Transaction } from "./common.js";
import type { Bundle } from "./bundle.js";
import type { CategoryCache } from "./categorize.js";
import type { CategoriesDoc } from "./seeds.js";
import {
  loadDataFolder,
  mergeIntoBundle,
  applyPlaidRemoved,
  filesSeenByMonth,
  persistAfterBatch,
  updateTransactionCategory,
  deleteTransaction,
  emptyBundle,
  addCategory,
  addSubcategory,
  removeSubcategory,
  TaxonomyError,
} from "./state.js";
import * as log from "./log.js";
import type { LogEntry } from "./log.js";
import {
  exchangeSetupToken,
  fetchAccounts,
} from "./aggregators/simplefin_client.js";
import {
  getItems as getSimplefinItems,
  addItem as addSimplefinItem,
  updateItem as updateSimplefinItem,
  removeItem as removeSimplefinItem,
  getSettings as getSimplefinSettings,
  saveSettings as saveSimplefinSettings,
  applyAccountOverrides,
  mergeAccountList,
  hiddenAccountLabels,
  type SimpleFinItem,
} from "./aggregators/simplefin_storage.js";
import * as vault from "./vault/vault.js";
import { ensureUnlocked } from "./vault/vault_ui.js";
import {
  getCredentials as getPlaidCreds,
  saveCredentials as savePlaidCreds,
  listItems as listPlaidItems,
  deleteItem as deletePlaidItem,
  hasAnyItem as hasAnyPlaidItem,
  updateItemAfterSync as updatePlaidItemAfterSync,
  clearAll as clearPlaidStorage,
  type PlaidItem,
  type KnownAccount,
} from "./aggregators/plaid_storage.js";
import {
  connectNewBank,
  pullAllItems,
  updateItemSelection,
  resetItemCursor,
  resetAllCursors,
  reLinkItem,
  maybeFlagRelink,
  resumeOAuthLinkIfPending,
  computeRedirectUri,
  type AccountPicker,
} from "./aggregators/plaid_orchestrator.js";
import {
  itemRemove,
  PlaidApiError,
  PLAID_TRANSACTIONS_COUNTRIES,
  DEFAULT_PLAID_COUNTRIES,
  type PlaidCredentials,
  type PlaidEnv,
} from "./aggregators/plaid_client.js";
import { pullPlaidSync, type SyncPullResult as PlaidSyncResult } from "./aggregators/plaid_sync.js";
import { versionLabel } from "./version.js";
import { buildDonutSegments, renderDonut } from "./donut.js";
import {
  type ViewFilter,
  emptyFilter,
  txMatches,
  loadFilter,
  saveFilter,
  filterOptions,
  downloadFilteredCsv,
} from "./view.js";

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing from DOM`);
  return el;
}
function show(id: string): void { $(id).classList.remove("hidden"); }
function hide(id: string): void { $(id).classList.add("hidden"); }

function setStatus(id: string, text: string, kind: "info" | "error" | "success" = "info"): void {
  const el = $(id);
  el.textContent = text;
  el.classList.remove("error", "success");
  if (kind === "error") el.classList.add("error");
  if (kind === "success") el.classList.add("success");
}

let currentFolder: FileSystemDirectoryHandle | null = null;
let currentFolderId: string = ""; // see folderFingerprint
let bundle: Bundle = emptyBundle();
let cache: CategoryCache = {};
let categories: CategoriesDoc = bundle.categories as CategoriesDoc;
// Multi-dimensional filter: month + category + subcategory + portal +
// account + income/expense kind + deleted-visibility. Persists per-
// folder in localStorage keyed by folder fingerprint (NOT name —
// two folders with the same name from different paths get distinct
// stable ids). See view.ts for the predicate + persistence helpers.
let currentFilter: ViewFilter = emptyFilter();
// Set of `tx.account` labels for SimpleFIN accounts the user has flagged
// hidden via the manage modal. Cached so renderSummary + renderTransactions
// don't hit IndexedDB on every keystroke. Refreshed after pulls, after
// account-override changes, and on boot.
let hiddenSimplefinLabels: Set<string> = new Set();

async function refreshHiddenSimplefinLabels(): Promise<void> {
  try {
    hiddenSimplefinLabels = hiddenAccountLabels(await getSimplefinItems());
  } catch {
    hiddenSimplefinLabels = new Set();
  }
}

function filterStorageKey(folderId: string): string {
  return `pf-filter-${folderId}`;
}

async function boot(): Promise<void> {
  log.subscribe(renderLogs);
  const version = versionLabel();
  log.info("ui", "App booted", { version });
  // Footer chip — visible to users, so when they screenshot the page
  // we can read which build they were on.
  const chip = document.getElementById("app-version-chip");
  if (chip) chip.textContent = version;
  registerServiceWorker();
  wireInstallPrompt();
  if (!isFolderPickerSupported()) {
    log.error("ui", "Browser missing File System Access API — gating to unsupported view");
    show("unsupported");
    return;
  }
  show("folder-picker");
  show("logs-section");
  $("logs-download").addEventListener("click", async () => {
    try {
      await log.downloadReport();
    } catch (err) {
      log.error("ui", "Save report failed", { error: String(err) });
    }
  });
  $("logs-copy").addEventListener("click", async () => {
    const btn = $("logs-copy") as HTMLButtonElement;
    const original = btn.textContent ?? "Copy to clipboard";
    btn.disabled = true;
    try {
      const bytes = await log.copyReportToClipboard();
      btn.textContent = `Copied ${bytes.toLocaleString()} chars ✓`;
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1800);
    } catch (err) {
      log.error("ui", "Copy log failed", { error: String(err) });
      btn.textContent = "Copy failed";
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1800);
    }
  });
  $("logs-clear").addEventListener("click", () => log.clear());
  renderLogs();

  // Manage-categories modal wiring. The button lives inside the workbench
  // card and stays disabled until a folder is connected (categories.json
  // doesn't exist to write to before then).
  $("manage-cats-btn").addEventListener("click", openCatModal);
  $("settings-btn").addEventListener("click", openSettingsModal);
  $("settings-modal-close").addEventListener("click", closeSettingsModal);
  $("settings-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeSettingsModal();
  });
  $("settings-change-folder").addEventListener("click", onSettingsChangeFolder);
  $("settings-clear-cache").addEventListener("click", onSettingsClearCache);
  $("settings-export-backup").addEventListener("click", onSettingsExportBackup);
  $("settings-show-tour").addEventListener("click", () => {
    closeSettingsModal();
    openWelcomeModal();
  });
  // Welcome / onboarding modal wiring + first-visit gate.
  $("welcome-modal-close").addEventListener("click", closeWelcomeModal);
  $("welcome-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeWelcomeModal();
  });
  $("welcome-start-btn").addEventListener("click", closeWelcomeModal);
  await maybeShowFirstRunWelcome();

  $("cat-modal-close").addEventListener("click", closeCatModal);
  $("cat-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeCatModal();
  });
  $("cat-new-add").addEventListener("click", onAddCategory);
  $("cat-sub-add").addEventListener("click", onAddSubcategory);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !$("cat-modal-backdrop").classList.contains("hidden")) {
      closeCatModal();
    }
  });
  // SimpleFIN wiring — buttons in the workbench, modal for setup token.
  $("simplefin-connect-btn").addEventListener("click", openSimplefinModal);
  $("simplefin-pull-btn").addEventListener("click", onSimplefinPullAll);
  $("simplefin-manage-btn").addEventListener("click", openSimplefinManageModal);
  $("sfin-modal-close").addEventListener("click", closeSimplefinModal);
  $("sfin-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeSimplefinModal();
  });
  $("sfin-connect-submit").addEventListener("click", onSimplefinConnectSubmit);
  // Manage modal wiring.
  $("sfin-manage-close").addEventListener("click", closeSimplefinManageModal);
  $("sfin-manage-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeSimplefinManageModal();
  });
  $("sfin-add-another-btn").addEventListener("click", () => {
    closeSimplefinManageModal();
    openSimplefinModal();
  });
  $("sfin-window-select").addEventListener("change", onSimplefinSettingsChange);
  $("sfin-autopull-select").addEventListener("change", onSimplefinSettingsChange);
  // Show the right SimpleFIN button based on stored state. Refresh the
  // hidden-account label cache first so the initial render of the
  // transactions table respects existing hides.
  await refreshHiddenSimplefinLabels();
  await refreshSimplefinButtons();

  // Plaid wiring — credentials modal, account selector modal, manage
  // banks modal. The vault gates every action behind ensureUnlocked();
  // the show/hide state of the buttons depends on what's in storage
  // PROVIDED the vault is unlocked (else we can't read them). So
  // refreshPlaidButtons is called both at boot (best-effort) and after
  // every state-changing action.
  $("plaid-connect-btn").addEventListener("click", onPlaidConnect);
  $("plaid-pull-btn").addEventListener("click", onPlaidPull);
  $("plaid-manage-btn").addEventListener("click", openPlaidManageModal);
  $("plaid-creds-modal-close").addEventListener("click", closePlaidCredsModal);
  $("plaid-creds-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closePlaidCredsModal();
  });
  $("plaid-creds-submit").addEventListener("click", onPlaidCredsSubmit);
  $("plaid-accounts-modal-close").addEventListener("click", () => onPlaidAccountsClose(false));
  $("plaid-accounts-cancel").addEventListener("click", () => onPlaidAccountsClose(false));
  $("plaid-accounts-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) onPlaidAccountsClose(false);
  });
  $("plaid-accounts-submit").addEventListener("click", () => onPlaidAccountsClose(true));
  $("plaid-manage-modal-close").addEventListener("click", closePlaidManageModal);
  $("plaid-manage-modal-backdrop").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closePlaidManageModal();
  });
  $("plaid-manage-add").addEventListener("click", onPlaidAddAnother);
  $("plaid-manage-reset-all").addEventListener("click", onPlaidResetAll);
  $("plaid-manage-edit-creds").addEventListener("click", onPlaidEditCreds);
  await refreshPlaidButtons();

  $("pick-folder-btn").addEventListener("click", onPickFolder);
  $("drop-zone").addEventListener("click", () => $("file-input").click());
  $("drop-zone").addEventListener("keydown", (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key === "Enter" || ke.key === " ") $("file-input").click();
  });
  $("file-input").addEventListener("change", onFilesPicked);
  const dz = $("drop-zone");
  dz.addEventListener("dragover", (e) => {
    e.preventDefault();
    dz.classList.add("dragover");
  });
  dz.addEventListener("dragleave", () => dz.classList.remove("dragover"));
  dz.addEventListener("drop", onDrop);

  // Try to recover a previously-picked folder. queryPermission returns
  // "granted" only if a tab from this session already has it; on a fresh
  // load we have to call requestPermission, which prompts the user once.
  try {
    const stored = await getStoredFolder();
    if (stored) await activateFolder(stored);
  } catch (err) {
    setStatus("folder-status", String(err), "error");
    await forgetStoredFolder();
  }

  // After folder + vault are settled, check whether this page load is
  // actually the OAuth-return leg of a Plaid Link session we opened
  // in a previous page (full-redirect OAuth pattern; happens on
  // mobile or when popups are blocked). The orchestrator handles the
  // detection — we just kick it off here.
  await maybeResumeOAuthLink();
}

async function onPickFolder(): Promise<void> {
  try {
    const handle = await pickDataFolder();
    await activateFolder(handle);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.toLowerCase().includes("aborted")) {
      setStatus("folder-status", "");
      return;
    }
    setStatus("folder-status", msg, "error");
  }
}

async function activateFolder(handle: FileSystemDirectoryHandle): Promise<void> {
  currentFolder = handle;
  setStatus("folder-status", `Loading "${handle.name}"…`);
  try {
    const state = await loadDataFolder(handle);
    bundle = state.bundle;
    cache = state.cache;
    categories = state.categories;
    const monthCount = state.knownMonths.length;
    const txCount = bundle.months.reduce((n, m) => n + m.transactions.length, 0);
    const tail =
      monthCount === 0
        ? "this folder is fresh — drop in some statements to get started."
        : `${txCount} transactions across ${monthCount} month${monthCount === 1 ? "" : "s"}, restored from disk.`;
    setStatus(
      "folder-status",
      `Connected to "${handle.name}" · ${tail}`,
      "success",
    );
    show("workbench");
    // Compute a stable id for the picked folder so the persisted filter
    // doesn't collide with a different folder of the same name. See
    // folderFingerprint for the scheme.
    currentFolderId = await folderFingerprint(handle);
    currentFilter = loadFilter(filterStorageKey(currentFolderId));
    // Render whatever was persisted so users see their state immediately,
    // before they process anything new.
    renderFilterBar();
    renderSummary();
    renderTransactions();
    // Fire-and-forget: if SimpleFIN is connected and stale, pull in the
    // background while the user looks at the existing data.
    void maybeAutoPull().catch((err) => {
      log.error("simplefin", "Auto-pull failed", { error: String(err) });
    });
  } catch (err) {
    setStatus("folder-status", `Couldn't read folder: ${err}`, "error");
  }
}

async function onFilesPicked(ev: Event): Promise<void> {
  const input = ev.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;
  await runProcessing([...input.files]);
  input.value = "";
}

async function onDrop(ev: DragEvent): Promise<void> {
  ev.preventDefault();
  $("drop-zone").classList.remove("dragover");
  const dt = ev.dataTransfer;
  if (!dt) return;
  const files: File[] = [];
  for (const item of Array.from(dt.items)) {
    const f = item.getAsFile();
    if (f) files.push(f);
  }
  if (files.length > 0) await runProcessing(files);
}

async function runProcessing(files: File[]): Promise<void> {
  if (!currentFolder) {
    setStatus("processing-status", "Pick a folder first.", "error");
    return;
  }
  setStatus("processing-status", `Reading ${files.length} file(s)…`);
  $("per-file-results").innerHTML = "";

  const inputs = await Promise.all(files.map(fileToInput));
  let result;
  try {
    result = await processFiles(inputs, { cache, categoriesDoc: categories });
  } catch (err) {
    setStatus("processing-status", String(err), "error");
    return;
  }

  // Merge newly-learned vendor categorizations into the cache so they
  // persist for future imports.
  Object.assign(cache, result.newlyLearned);

  // Merge fresh transactions into the in-memory bundle.
  const filesSeen = filesSeenByMonth(result.perFile);
  const touched = mergeIntoBundle(bundle, result.transactions, filesSeen);

  // Apply any Plaid `removed` IDs the parsed files surfaced. Matching
  // existing transactions get flipped to deleted = true; the months
  // they live in are unioned into the persist set so the writes pick
  // them up.
  if (result.removedPlaidIds.size > 0) {
    const removedTouched = applyPlaidRemoved(bundle, result.removedPlaidIds);
    for (const m of removedTouched) touched.add(m);
    log.info("plaid", "Applied Plaid removed[]", {
      removedSeen: result.removedPlaidIds.size,
      monthsTouched: removedTouched.size,
    });
  }

  // Persist everything. Failures here are surfaced — losing a write
  // means the next visit won't see this batch.
  try {
    await persistAfterBatch(currentFolder, bundle, cache, touched);
  } catch (err) {
    setStatus("processing-status", `Saved in memory, but disk write failed: ${err}`, "error");
    return;
  }

  renderPerFile(result.perFile, inputs);
  renderFilterBar();
  renderSummary();
  renderTransactions();
  setStatus(
    "processing-status",
    `Processed ${result.perFile.length} file(s) · ${result.transactions.length} new transactions · saved.`,
    "success",
  );
}

interface PerFileRow {
  filename: string;
  portal: string;
  transactions: Transaction[];
  error: string;
}

// Common errors we know how to coach the user past. Order matters —
// first matching pattern wins.
const ERROR_HINTS: Array<{ test: RegExp; hint: string }> = [
  {
    test: /scanned|no transaction-shaped lines|couldn't find any transaction/i,
    hint:
      "If this is a scanned/image PDF, the parser can't read it. Most banks let you export a CSV — that's the most reliable format.",
  },
  {
    test: /detect a tabular structure|tabular structure|CSV.*header row/i,
    hint:
      "The file may not have a header row, or it may not be CSV. If the file is named with the bank's name (e.g. 'bmo_chequing.csv'), the parser will use a fallback.",
  },
  {
    test: /Unsupported file type/i,
    hint: "Only .csv and .pdf files are recognised. Convert XLS / OFX / QFX to CSV first.",
  },
];

function fileHint(error: string): string {
  for (const h of ERROR_HINTS) if (h.test.test(error)) return h.hint;
  return "";
}

// Per-file diagnostic — small JSON the user can save when reporting a
// bug with a specific file. Deliberately omits the file BYTES (privacy
// posture: nothing leaves the machine unless the user copies + emails
// it themselves). Includes the file's metadata + the most recent log
// entries, which usually capture the failure shape.
function buildFileDiagnostic(r: PerFileRow, sizeBytes: number | null): string {
  return JSON.stringify(
    {
      filename: r.filename,
      portal_detected: r.portal,
      transactions_parsed: r.transactions.length,
      error: r.error,
      file_size_bytes: sizeBytes,
      timestamp: new Date().toISOString(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      recent_logs: log.getEntries().slice(-50),
    },
    null,
    2,
  );
}

function downloadText(content: string, filename: string, mime = "text/plain"): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function renderPerFile(rows: PerFileRow[], inputs: InputFile[] = []): void {
  const container = $("per-file-results");
  container.innerHTML = "";
  const sizeByName = new Map(inputs.map((f) => [f.filename, f.bytes.length]));
  for (const r of rows) {
    const row = document.createElement("div");
    row.classList.add("file-row");
    if (r.error) row.classList.add("error");

    const summary = document.createElement("div");
    summary.classList.add("file-row-summary");
    const fn = document.createElement("span");
    fn.classList.add("filename");
    fn.textContent = r.filename;
    summary.appendChild(fn);
    const portal = document.createElement("span");
    portal.classList.add("portal");
    if (r.error) portal.textContent = ` — failed to parse`;
    else portal.textContent = ` — ${r.portal} · ${r.transactions.length} txs`;
    summary.appendChild(portal);

    if (r.error) {
      // Make the row a toggle. Only build the expanded payload when the
      // user actually clicks — keeps the initial render cheap when 50+
      // files are processed.
      summary.classList.add("clickable");
      const caret = document.createElement("span");
      caret.classList.add("file-row-caret");
      caret.textContent = "▸";
      summary.prepend(caret);
      row.appendChild(summary);

      const details = document.createElement("div");
      details.classList.add("file-row-details", "hidden");
      const errBlock = document.createElement("pre");
      errBlock.classList.add("file-row-error");
      errBlock.textContent = r.error;
      details.appendChild(errBlock);

      const hint = fileHint(r.error);
      if (hint) {
        const hintEl = document.createElement("p");
        hintEl.classList.add("file-row-hint");
        hintEl.textContent = hint;
        details.appendChild(hintEl);
      }

      const actions = document.createElement("div");
      actions.classList.add("file-row-actions");
      const copyBtn = document.createElement("button");
      copyBtn.type = "button";
      copyBtn.classList.add("link-btn");
      copyBtn.textContent = "Copy error";
      copyBtn.addEventListener("click", async (ev) => {
        ev.stopPropagation();
        try {
          await navigator.clipboard.writeText(r.error);
          copyBtn.textContent = "Copied!";
          setTimeout(() => (copyBtn.textContent = "Copy error"), 1500);
        } catch {
          copyBtn.textContent = "Copy failed (select manually)";
        }
      });
      actions.appendChild(copyBtn);

      const reportBtn = document.createElement("button");
      reportBtn.type = "button";
      reportBtn.classList.add("link-btn");
      reportBtn.textContent = "Save diagnostic";
      reportBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        const json = buildFileDiagnostic(r, sizeByName.get(r.filename) ?? null);
        const safeName = r.filename.replace(/[^\w.\-]/g, "_");
        downloadText(json, `pfd-diagnostic-${safeName}-${Date.now()}.json`, "application/json");
      });
      actions.appendChild(reportBtn);
      details.appendChild(actions);

      row.appendChild(details);

      summary.addEventListener("click", () => {
        const isOpen = details.classList.toggle("hidden") === false;
        caret.textContent = isOpen ? "▾" : "▸";
      });
    } else {
      row.appendChild(summary);
    }

    container.appendChild(row);
  }
}

function renderFilterBar(): void {
  const bar = $("filter-bar");
  bar.innerHTML = "";
  if (bundle.months.length === 0) {
    hide("filter-bar");
    return;
  }
  show("filter-bar");

  // ── Row 1: month / year pills ────────────────────────────────────
  const monthRow = document.createElement("div");
  monthRow.classList.add("filter-row", "filter-pills");
  const monthsDesc = [...bundle.months.map((m) => m.month)].sort((a, b) => b.localeCompare(a));
  const years = [...new Set(monthsDesc.map((m) => m.slice(0, 4)))];
  type Opt = { value: string; label: string };
  const monthOpts: Opt[] = [{ value: "all", label: "All time" }];
  for (const y of years) monthOpts.push({ value: y, label: y });
  monthOpts.push({ value: "__sep__", label: "" });
  for (const m of monthsDesc) {
    const [year, mm] = m.split("-");
    const monName = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ][parseInt(mm, 10) - 1] || mm;
    monthOpts.push({ value: m, label: `${monName} ${year}` });
  }
  if (!monthOpts.some((o) => o.value === currentFilter.month)) currentFilter.month = "all";
  for (const opt of monthOpts) {
    if (opt.value === "__sep__") {
      const sepEl = document.createElement("div");
      sepEl.classList.add("filter-separator");
      monthRow.appendChild(sepEl);
      continue;
    }
    const pill = document.createElement("button");
    pill.type = "button";
    pill.classList.add("filter-pill");
    if (opt.value === currentFilter.month) pill.classList.add("active");
    pill.textContent = opt.label;
    pill.addEventListener("click", () => setFilter({ month: opt.value }));
    monthRow.appendChild(pill);
  }
  bar.appendChild(monthRow);

  // ── Row 2: category / subcategory / portal / account / kind ─────
  // Options computed from the data — only show choices that exist in
  // the bundle, so users never pick a dead-end filter. Hidden SimpleFIN
  // accounts are pre-filtered so they don't pollute the Account dropdown
  // with options that select nothing.
  const allTxs = bundle.months
    .flatMap((m) => m.transactions)
    .filter((t) => !(t.portal === "simplefin" && hiddenSimplefinLabels.has(t.account)));
  const opts = filterOptions(allTxs);

  const dimRow = document.createElement("div");
  dimRow.classList.add("filter-row", "filter-selects");
  dimRow.appendChild(buildFilterSelect("category", "Category", ["all", ...opts.categories], currentFilter.category, (v) => setFilter({ category: v })));
  // Subcategories filtered by selected category so picker stays coherent.
  const subSourceCats = currentFilter.category === "all"
    ? opts.subcategories
    : opts.subcategories.filter((q) => q.startsWith(`${currentFilter.category} / `));
  dimRow.appendChild(buildFilterSelect("subcategory", "Subcategory", ["all", ...subSourceCats], currentFilter.subcategory, (v) => setFilter({ subcategory: v })));
  dimRow.appendChild(buildFilterSelect("portal", "Portal", ["all", ...opts.portals], currentFilter.portal, (v) => setFilter({ portal: v })));
  dimRow.appendChild(buildFilterSelect("account", "Account", ["all", ...opts.accounts], currentFilter.account, (v) => setFilter({ account: v })));
  if (opts.last4s.length > 0) {
    dimRow.appendChild(buildFilterSelect("last4", "Last 4", ["all", ...opts.last4s], currentFilter.last4, (v) => setFilter({ last4: v })));
  }
  dimRow.appendChild(buildFilterSelect("kind", "Type", ["all", "income", "expense", "transfer"], currentFilter.kind, (v) => setFilter({ kind: v as ViewFilter["kind"] })));
  dimRow.appendChild(buildFilterSelect("deleted", "Deleted", ["active", "all", "only-deleted"], currentFilter.deleted, (v) => setFilter({ deleted: v as ViewFilter["deleted"] })));

  // Action group: reset + download CSV. Right-aligned via flex.
  const actions = document.createElement("div");
  actions.classList.add("filter-actions");
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.classList.add("link-btn");
  resetBtn.textContent = "Reset filters";
  resetBtn.addEventListener("click", () => setFilter(emptyFilter()));
  actions.appendChild(resetBtn);
  const csvBtn = document.createElement("button");
  csvBtn.type = "button";
  csvBtn.classList.add("secondary");
  csvBtn.textContent = "Download CSV";
  csvBtn.addEventListener("click", onDownloadCsv);
  actions.appendChild(csvBtn);
  dimRow.appendChild(actions);

  bar.appendChild(dimRow);
}

function buildFilterSelect(
  dim: string,
  label: string,
  values: string[],
  selected: string,
  onChange: (v: string) => void,
): HTMLElement {
  const wrap = document.createElement("label");
  wrap.classList.add("filter-select-wrap");
  const labelEl = document.createElement("span");
  labelEl.classList.add("filter-select-label");
  labelEl.textContent = label;
  wrap.appendChild(labelEl);
  const sel = document.createElement("select");
  sel.classList.add("filter-select");
  sel.dataset.dim = dim;
  for (const v of values) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.textContent = v === "all" ? "All" : v;
    if (v === selected) opt.selected = true;
    sel.appendChild(opt);
  }
  // If the currently-selected value isn't in the option list (e.g., the
  // category was deleted), the select silently picks the first option.
  // Mirror that to currentFilter so the predicate doesn't keep filtering
  // by a now-orphaned value.
  if (!values.includes(selected)) {
    queueMicrotask(() => onChange(values[0]));
  }
  sel.addEventListener("change", () => onChange(sel.value));
  wrap.appendChild(sel);
  return wrap;
}

function setFilter(patch: Partial<ViewFilter>): void {
  const next: ViewFilter = { ...currentFilter, ...patch };
  // Reset subcategory if the parent category changed and the previously-
  // selected sub no longer belongs to the new category.
  if (patch.category && next.subcategory !== "all" && !next.subcategory.startsWith(`${next.category} / `)) {
    next.subcategory = "all";
  }
  // Same shape: shallow-compare to avoid re-renders on no-op clicks.
  if (
    next.month === currentFilter.month &&
    next.category === currentFilter.category &&
    next.subcategory === currentFilter.subcategory &&
    next.portal === currentFilter.portal &&
    next.account === currentFilter.account &&
    next.last4 === currentFilter.last4 &&
    next.kind === currentFilter.kind &&
    next.deleted === currentFilter.deleted
  ) return;
  currentFilter = next;
  if (currentFolderId) saveFilter(filterStorageKey(currentFolderId), currentFilter);
  renderFilterBar();
  renderSummary();
  renderTransactions();
}

async function onDownloadCsv(): Promise<void> {
  const txs = allTransactions(); // already filtered
  if (txs.length === 0) {
    setStatus("processing-status", "Nothing in the current view to export.", "error");
    return;
  }
  try {
    downloadFilteredCsv(txs, currentFilter);
    log.info("ui", `CSV exported`, { rows: txs.length });
  } catch (err) {
    log.error("ui", "CSV export failed", { error: String(err) });
    setStatus("processing-status", `Download failed: ${err}`, "error");
  }
}

function renderSummary(): void {
  const container = $("summary-pane");
  container.innerHTML = "";
  // Each month card is built from the FILTERED transactions in that
  // month — not the precomputed m.summary (which is unfiltered).
  // That way picking "Restaurants only" updates the totals to just
  // restaurant spend.
  const visibleMonths: Array<{ month: string; txs: Transaction[] }> = [];
  for (const m of bundle.months) {
    const filtered = m.transactions.filter((t) => txMatches(t, currentFilter, hiddenSimplefinLabels));
    if (filtered.length > 0) visibleMonths.push({ month: m.month, txs: filtered });
  }
  if (visibleMonths.length === 0) {
    hide("summary-pane");
    return;
  }
  show("summary-pane");
  visibleMonths.sort((a, b) => b.month.localeCompare(a.month));
  for (const { month, txs } of visibleMonths) {
    const card = document.createElement("div");
    card.classList.add("month-card");
    card.appendChild(renderFilteredMonthCard(month, txs));
    container.appendChild(card);
  }
}

/** Compute per-month income/expense/by-category from a filtered tx set.
 *  Lifted out so renderFilteredMonthCard can call it without going
 *  through the full summarize() (which always operates on the
 *  unfiltered set). */
function computeSummary(txs: Transaction[]): {
  income: number;
  expense: number;
  count: number;
  byCategory: Record<string, number>;
} {
  // Caller's filter is the gate (txMatches already excluded `deleted`
  // rows when the active filter is "active"). When the user is on
  // "only-deleted" view, this still gives meaningful totals so they
  // can see what they removed.
  let income = 0;
  let expense = 0;
  let count = 0;
  const byCategory: Record<string, number> = {};
  for (const t of txs) {
    count++;
    if (t.amount < 0) income += -t.amount;
    else {
      expense += t.amount;
      byCategory[t.category] = (byCategory[t.category] ?? 0) + t.amount;
    }
  }
  return { income, expense, count, byCategory };
}

function renderFilteredMonthCard(month: string, txs: Transaction[]): DocumentFragment {
  const f = document.createDocumentFragment();
  const h = document.createElement("h3");
  h.textContent = month;
  f.appendChild(h);

  const s = computeSummary(txs);
  const profit = s.income - s.expense;

  const totals = document.createElement("div");
  totals.classList.add("totals-row");
  // Display sign convention is user-facing (income +, expense -), opposite
  // of the internal storage convention (income negative, expense positive).
  // The CSS classes `.positive` / `.negative` are colored green / red.
  totals.innerHTML = `
    <div class="totals-cell">
      <div class="totals-label">Income</div>
      <div class="totals-value positive">${formatAmount(s.income)}</div>
    </div>
    <div class="totals-cell">
      <div class="totals-label">Expense</div>
      <div class="totals-value negative">${formatAmount(-s.expense)}</div>
    </div>
    <div class="totals-cell">
      <div class="totals-label">${profit >= 0 ? "Profit" : "Loss"}</div>
      <div class="totals-value ${profit >= 0 ? "positive" : "negative"}">${formatAmount(Math.abs(profit))}</div>
    </div>
    <div class="totals-cell">
      <div class="totals-label">Transactions</div>
      <div class="totals-value">${s.count}</div>
    </div>
  `;
  f.appendChild(totals);

  // PnL strip — horizontal stacked bar showing income vs expense
  // proportions, with profit/loss callout below. Only meaningful when
  // there's at least one income OR expense to visualize.
  if (s.income > 0 || s.expense > 0) {
    f.appendChild(renderPnlStrip(s.income, s.expense, profit));
  }

  const topCats = Object.entries(s.byCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  if (topCats.length > 0) {
    // Two-column layout: donut on the left, top-5 bar list on the right.
    // The donut adds visual weight; the list keeps the precise numbers.
    const wrap = document.createElement("div");
    wrap.classList.add("cat-row-wrap");

    const segments = buildDonutSegments(s.byCategory);
    const donut = renderDonut(segments);
    if (donut) {
      const donutWrap = document.createElement("div");
      donutWrap.classList.add("donut-wrap");
      donutWrap.appendChild(donut);
      wrap.appendChild(donutWrap);
    }

    const max = topCats[0][1] || 1;
    const list = document.createElement("ul");
    list.classList.add("cat-bars");
    for (const [name, amount] of topCats) {
      const li = document.createElement("li");
      const pct = Math.round((amount / max) * 100);
      li.innerHTML = `
        <div class="cat-row">
          <span class="cat-name">${escapeHtml(name)}</span>
          <span class="cat-amount">${formatAmount(amount)}</span>
        </div>
        <div class="cat-bar"><div class="cat-bar-fill" style="width: ${pct}%"></div></div>
      `;
      list.appendChild(li);
    }
    wrap.appendChild(list);
    f.appendChild(wrap);
  }
  return f;
}

function allTransactions(): Transaction[] {
  // Respect the active filter — table + CSV export both read through here.
  return bundle.months
    .flatMap((m) => m.transactions)
    .filter((t) => txMatches(t, currentFilter, hiddenSimplefinLabels));
}

/** Horizontal stacked bar: income (left, green) vs expense (right,
 *  orange) proportions, with profit/loss callout below. Width split
 *  is per-month so each card is self-contained. */
function renderPnlStrip(income: number, expense: number, profit: number): HTMLElement {
  const div = document.createElement("div");
  div.classList.add("pnl-strip");
  const total = income + expense;
  // Avoid zero-width segments — show a min 6% sliver so the bar reads
  // as "has both" rather than "either side empty".
  const incomeShare = total > 0 ? (income / total) * 100 : 50;
  const expenseShare = total > 0 ? (expense / total) * 100 : 50;
  const incomeWidth = income > 0 ? Math.max(incomeShare, 6) : 0;
  const expenseWidth = expense > 0 ? Math.max(expenseShare, 6) : 0;
  div.innerHTML = `
    <div class="pnl-bar" role="img" aria-label="Income ${incomeShare.toFixed(0)}% vs Expense ${expenseShare.toFixed(0)}%">
      <div class="pnl-income" style="width: ${incomeWidth}%"
           title="Income ${formatAmount(income)} (${incomeShare.toFixed(0)}%)"></div>
      <div class="pnl-expense" style="width: ${expenseWidth}%"
           title="Expense ${formatAmount(expense)} (${expenseShare.toFixed(0)}%)"></div>
    </div>
    <div class="pnl-legend">
      <span class="pnl-tag pnl-tag-income">● Income</span>
      <span class="pnl-tag pnl-tag-expense">● Expense</span>
      <span class="pnl-tag pnl-tag-profit ${profit >= 0 ? "is-profit" : "is-loss"}">
        ${profit >= 0 ? "Saved" : "Loss"} ${formatAmount(Math.abs(profit))}
        ${income > 0 ? `(${Math.round((profit / income) * 100)}% of income)` : ""}
      </span>
    </div>
  `;
  return div;
}

function renderTransactions(): void {
  const txs = allTransactions();
  renderReconBanner();
  if (txs.length === 0) {
    hide("transactions-heading");
    hide("transactions-table");
    $("transactions-summary").textContent = "";
    return;
  }
  show("transactions-heading");
  $("transactions-summary").textContent = `${txs.length} transactions across ${
    new Set(txs.map((t) => t.date.slice(0, 7))).size
  } month(s).`;
  const table = $("transactions-table") as HTMLTableElement;
  show("transactions-table");
  // Build the header row.
  const thead = table.tHead!;
  thead.innerHTML = `
    <tr>
      <th>Date</th>
      <th>Portal</th>
      <th>Account</th>
      <th>Vendor</th>
      <th>Category</th>
      <th class="num">Amount</th>
      <th></th>
    </tr>
  `;
  const tbody = table.tBodies[0];
  tbody.innerHTML = "";
  const sorted = [...txs].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  for (const t of sorted) {
    const tr = document.createElement("tr");
    tr.dataset.txId = t.id;
    if (t.deleted) tr.classList.add("tx-deleted");
    tr.innerHTML = `
      <td>${t.date}</td>
      <td>${escapeHtml(portalDisplay(t))}</td>
      <td>${escapeHtml(t.account)}</td>
      <td>${escapeHtml(t.vendor_raw)}</td>
      <td class="cat-cell"></td>
      <td class="num ${t.amount < 0 ? "positive" : "negative"}">${formatAmount(-t.amount)}</td>
      <td class="tx-actions"></td>
    `;
    const catCell = tr.querySelector(".cat-cell") as HTMLElement;
    catCell.appendChild(buildCategorySelect(t));
    const actionsCell = tr.querySelector(".tx-actions") as HTMLElement;
    actionsCell.appendChild(buildDeleteButton(t));
    tbody.appendChild(tr);
  }
}

function buildDeleteButton(tx: Transaction): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.classList.add("tx-delete-btn");
  btn.dataset.txId = tx.id;
  // Same affordance for delete + undo so the row stays visible in
  // "all" or "only-deleted" view and can be restored without a separate
  // control. Title surfaces the action; the icon flips with state.
  btn.title = tx.deleted ? "Restore this transaction" : "Delete this transaction";
  btn.setAttribute("aria-label", btn.title);
  btn.textContent = tx.deleted ? "↺" : "×";
  btn.addEventListener("click", () => onDeleteClick(tx.id, !tx.deleted));
  return btn;
}

async function onDeleteClick(txId: string, deleted: boolean): Promise<void> {
  if (!currentFolder) return;
  try {
    const result = await deleteTransaction(currentFolder, bundle, cache, txId, deleted);
    if (!result) {
      setStatus("processing-status", `Transaction not found: ${txId}`, "error");
      return;
    }
    renderFilterBar(); // tx counts in dropdowns may change
    renderSummary();
    renderTransactions();
    setStatus(
      "processing-status",
      deleted
        ? `Deleted: ${result.updated.vendor_raw}`
        : `Restored: ${result.updated.vendor_raw}`,
      "success",
    );
  } catch (err) {
    setStatus("processing-status", `Save failed: ${err}`, "error");
  }
}

// One <select> with flattened "Category / Subcategory" options. Simpler
// than nested dropdowns; matches the desktop dashboard's editor.
// Sentinel value for the inline "+ Add new..." option in the category
// dropdown. Triggers the Manage-categories modal instead of being
// treated as a real category/subcategory pair.
const ADD_NEW_CATEGORY_VALUE = "__add_new__";

function buildCategorySelect(tx: Transaction): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.classList.add("cat-select");
  sel.dataset.txId = tx.id;
  // Track the previous value so a cancelled-out "+ Add new" returns the
  // select to where the user was — they didn't change the category, they
  // opened a modal.
  let lastValue = `${tx.category}|${tx.subcategory}`;
  for (const cat of categories.categories) {
    for (const sub of cat.subcategories) {
      const opt = document.createElement("option");
      opt.value = `${cat.name}|${sub}`;
      opt.textContent = `${cat.name} / ${sub}`;
      if (cat.name === tx.category && sub === tx.subcategory) {
        opt.selected = true;
        lastValue = opt.value;
      }
      sel.appendChild(opt);
    }
  }
  // Visual separator + inline shortcut to the Manage-categories modal,
  // so users don't have to leave the row to add a missing category.
  const sep = document.createElement("option");
  sep.disabled = true;
  sep.textContent = "──────────";
  sel.appendChild(sep);
  const addNew = document.createElement("option");
  addNew.value = ADD_NEW_CATEGORY_VALUE;
  addNew.textContent = "+ Add new category…";
  sel.appendChild(addNew);
  sel.dataset.lastValue = lastValue;
  sel.addEventListener("change", () => onCategoryChange(sel));
  return sel;
}

async function onCategoryChange(sel: HTMLSelectElement): Promise<void> {
  // "+ Add new category…" → open Manage modal; restore the prior selection.
  if (sel.value === ADD_NEW_CATEGORY_VALUE) {
    sel.value = sel.dataset.lastValue ?? "";
    openCatModal();
    return;
  }
  if (!currentFolder) return;
  const txId = sel.dataset.txId!;
  const [category, subcategory] = sel.value.split("|", 2);
  // Remember the new value so a subsequent "+ Add new" rollback is correct.
  sel.dataset.lastValue = sel.value;
  sel.disabled = true;
  try {
    const result = await updateTransactionCategory(
      currentFolder,
      bundle,
      cache,
      txId,
      category,
      subcategory,
    );
    if (!result) {
      setStatus("processing-status", `Transaction not found: ${txId}`, "error");
      return;
    }
    // Only the affected month's summary changed — re-render summary cards
    // so totals update. Table rows stay where they are; the <select> already
    // reflects the new value.
    renderSummary();
    setStatus(
      "processing-status",
      `Saved: ${result.updated.vendor_raw} → ${category} / ${subcategory}`,
      "success",
    );
  } catch (err) {
    setStatus("processing-status", `Save failed: ${err}`, "error");
  } finally {
    sel.disabled = false;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Manage-categories modal
// ─────────────────────────────────────────────────────────────────────

function openCatModal(): void {
  if (!currentFolder) {
    setStatus("processing-status", "Pick a folder first.", "error");
    return;
  }
  renderCatModal();
  show("cat-modal-backdrop");
  // Focus the first input for keyboard-only users.
  setTimeout(() => ($("cat-new-name") as HTMLInputElement).focus(), 0);
}

function closeCatModal(): void {
  hide("cat-modal-backdrop");
  setStatus("cat-new-status", "");
  setStatus("cat-sub-status", "");
}

function renderCatModal(): void {
  // 1) Refresh the "parent category" dropdown for add-subcategory.
  const parentSel = $("cat-sub-parent") as HTMLSelectElement;
  parentSel.innerHTML = "";
  for (const cat of categories.categories) {
    const opt = document.createElement("option");
    opt.value = cat.name;
    opt.textContent = `${cat.name} (${cat.kind})`;
    parentSel.appendChild(opt);
  }
  // 2) Re-render the existing-taxonomy list.
  const list = $("cat-list");
  list.innerHTML = "";
  for (const cat of categories.categories) {
    const item = document.createElement("div");
    item.classList.add("cat-list-item", `kind-${cat.kind}`);
    const head = document.createElement("div");
    head.classList.add("cli-head");
    head.innerHTML = `
      <span class="cli-name">${escapeHtml(cat.name)}</span>
      <span class="cli-kind">${cat.kind}</span>
    `;
    item.appendChild(head);
    const subs = document.createElement("div");
    subs.classList.add("cli-subs");
    for (const sub of cat.subcategories) {
      const chip = document.createElement("span");
      chip.classList.add("cli-sub-chip");
      chip.appendChild(document.createTextNode(sub));
      const x = document.createElement("button");
      x.type = "button";
      x.textContent = "×";
      x.title = `Remove ${sub}`;
      x.setAttribute("aria-label", `Remove ${sub}`);
      x.addEventListener("click", () => onRemoveSubcategory(cat.name, sub));
      chip.appendChild(x);
      subs.appendChild(chip);
    }
    item.appendChild(subs);
    list.appendChild(item);
  }
}

async function onAddCategory(): Promise<void> {
  if (!currentFolder) return;
  const name = ($("cat-new-name") as HTMLInputElement).value;
  const kind = ($("cat-new-kind") as HTMLSelectElement).value as "income" | "expense" | "transfer";
  const subsRaw = ($("cat-new-subs") as HTMLTextAreaElement).value;
  const subs = subsRaw.split(/\r?\n/);
  try {
    await addCategory(currentFolder, categories, { name, kind, subcategories: subs });
    setStatus("cat-new-status", `Added "${name.trim()}".`, "success");
    ($("cat-new-name") as HTMLInputElement).value = "";
    ($("cat-new-subs") as HTMLTextAreaElement).value = "";
    renderCatModal();
    // Re-render the transaction table so newly-added categories appear
    // in inline dropdowns immediately.
    renderTransactions();
  } catch (err) {
    const msg = err instanceof TaxonomyError ? err.message : `Save failed: ${err}`;
    setStatus("cat-new-status", msg, "error");
  }
}

async function onAddSubcategory(): Promise<void> {
  if (!currentFolder) return;
  const parent = ($("cat-sub-parent") as HTMLSelectElement).value;
  const sub = ($("cat-sub-name") as HTMLInputElement).value;
  try {
    await addSubcategory(currentFolder, categories, parent, sub);
    setStatus("cat-sub-status", `Added "${sub.trim()}" to "${parent}".`, "success");
    ($("cat-sub-name") as HTMLInputElement).value = "";
    renderCatModal();
    renderTransactions();
  } catch (err) {
    const msg = err instanceof TaxonomyError ? err.message : `Save failed: ${err}`;
    setStatus("cat-sub-status", msg, "error");
  }
}

async function onRemoveSubcategory(parent: string, sub: string): Promise<void> {
  if (!currentFolder) return;
  try {
    await removeSubcategory(currentFolder, categories, parent, sub);
    renderCatModal();
    renderTransactions();
  } catch (err) {
    const msg = err instanceof TaxonomyError ? err.message : `Remove failed: ${err}`;
    setStatus("cat-sub-status", msg, "error");
  }
}

// Aggregator-sourced txs render as "BMO (via Plaid)" / "BMO (via SimpleFIN)"
// when institution_name is set — mirrors the desktop's portalDisplay helper.
// Falls back to bare portal for non-aggregator sources.
function portalDisplay(t: Transaction): string {
  if (t.portal === "plaid" && t.institution_name) return `${t.institution_name} (via Plaid)`;
  if (t.portal === "simplefin" && t.institution_name) return `${t.institution_name} (via SimpleFIN)`;
  return t.portal;
}

function formatAmount(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Render the bottom log panel. Cheap to call on every log() event — at
// MAX_BUFFER=500 it's a single innerHTML replace, well under one frame.
function renderLogs(): void {
  const list = document.getElementById("logs-list");
  if (!list) return;
  const entries = log.getEntries();
  if (entries.length === 0) {
    list.innerHTML = `<div class="log-empty">No activity yet — drop in a file or pick a folder.</div>`;
  } else {
    // Newest on top so the most recent failure is visible without scrolling.
    const rows: string[] = [];
    for (let i = entries.length - 1; i >= 0; i--) {
      rows.push(renderLogRow(entries[i]));
    }
    list.innerHTML = rows.join("");
  }
  const c = log.counts();
  const countsEl = document.getElementById("logs-counts");
  if (countsEl) {
    const parts: string[] = [];
    if (c.error) parts.push(`<span class="badge error">${c.error} error${c.error === 1 ? "" : "s"}</span>`);
    if (c.warn) parts.push(`<span class="badge warn">${c.warn} warn${c.warn === 1 ? "" : "s"}</span>`);
    if (c.info) parts.push(`<span class="badge info">${c.info} info</span>`);
    countsEl.innerHTML = parts.join("");
  }
}

function renderLogRow(e: LogEntry): string {
  // HH:MM:SS from an ISO timestamp — enough resolution to correlate without
  // taking up half the row width.
  const ts = e.ts.slice(11, 19);
  const details = e.details
    ? ` <span style="color:var(--muted)">${escapeHtml(JSON.stringify(e.details))}</span>`
    : "";
  return `
    <div class="log-row">
      <span class="log-ts">${ts}</span>
      <span class="log-level ${e.level}">${e.level}</span>
      <span class="log-source">${escapeHtml(e.source)}</span>
      <span class="log-msg">${escapeHtml(e.message)}${details}</span>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─────────────────────────────────────────────────────────────────────
// Welcome / onboarding — first-visit modal
// ─────────────────────────────────────────────────────────────────────

import { get as idbGet, set as idbSet } from "idb-keyval";

const TOUR_SEEN_KEY = "pf:tour-seen";

async function maybeShowFirstRunWelcome(): Promise<void> {
  // Soft-fail if IndexedDB is unavailable — better to skip the tour
  // than to crash boot. The modal can still be opened from Settings.
  try {
    const seen = await idbGet<boolean>(TOUR_SEEN_KEY);
    if (!seen) openWelcomeModal();
  } catch (err) {
    log.warn("ui", "Could not read tour-seen flag", { error: String(err) });
  }
}

function openWelcomeModal(): void {
  show("welcome-modal-backdrop");
}

function closeWelcomeModal(): void {
  hide("welcome-modal-backdrop");
  // Persist the dismissal so future boots skip the auto-open. Soft-fail
  // on write — if IDB is broken the user just sees the modal again,
  // which is annoying but not blocking.
  idbSet(TOUR_SEEN_KEY, true).catch((err) =>
    log.warn("ui", "Could not persist tour-seen flag", { error: String(err) }),
  );
}

// ─────────────────────────────────────────────────────────────────────
// PWA — service worker registration + Install button
// ─────────────────────────────────────────────────────────────────────

function registerServiceWorker(): void {
  // Skip in dev — Vite's HMR + a stale SW cache is a debugging nightmare.
  // We register only on a deployed build (typically https + a real host).
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    log.info("pwa", "Service worker skipped on localhost");
    return;
  }
  navigator.serviceWorker
    .register("/sw.js")
    .then(() => log.info("pwa", "Service worker registered"))
    .catch((err) => log.warn("pwa", "Service worker registration failed", { error: String(err) }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let deferredInstallPrompt: any = null;

function wireInstallPrompt(): void {
  // Chrome/Edge fire this when the manifest + SW prerequisites are met
  // AND the user hasn't already installed. We stash the event and show
  // an Install button in the header. Clicking calls prompt() — that's
  // the only way to trigger the native install UI from script.
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    show("install-app-btn");
    log.info("pwa", "Install prompt available");
  });
  window.addEventListener("appinstalled", () => {
    hide("install-app-btn");
    deferredInstallPrompt = null;
    log.info("pwa", "App installed");
  });
  $("install-app-btn").addEventListener("click", async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    log.info("pwa", `Install prompt outcome: ${outcome}`);
    deferredInstallPrompt = null;
    if (outcome === "accepted") hide("install-app-btn");
  });
}

// ─────────────────────────────────────────────────────────────────────
// Settings modal — change folder, clear cache, export backup
// ─────────────────────────────────────────────────────────────────────

import { saveCategoryCache } from "./state.js";

function openSettingsModal(): void {
  if (!currentFolder) {
    setStatus("processing-status", "Pick a folder first.", "error");
    return;
  }
  $("settings-folder-current").textContent = `Currently using: ${currentFolder.name}`;
  setStatus("settings-cache-status", "");
  setStatus("settings-export-status", "");
  show("settings-modal-backdrop");
}

function closeSettingsModal(): void {
  hide("settings-modal-backdrop");
}

async function onSettingsChangeFolder(): Promise<void> {
  closeSettingsModal();
  await onPickFolder(); // existing folder-picker flow, including activate + load
}

async function onSettingsClearCache(): Promise<void> {
  if (!currentFolder) return;
  const ok = confirm(
    "Clear the category cache? Your transactions and categories stay; only the\nlearned vendor-mappings (Tim Hortons → Restaurants etc.) are dropped.\nFuture imports will fall back to rule defaults.",
  );
  if (!ok) return;
  setStatus("settings-cache-status", "Clearing…");
  try {
    for (const k of Object.keys(cache)) delete cache[k];
    await saveCategoryCache(currentFolder, cache);
    log.info("settings", "Category cache cleared");
    setStatus("settings-cache-status", "Cache cleared.", "success");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus("settings-cache-status", `Failed: ${msg}`, "error");
    log.error("settings", "Clear cache failed", { error: msg });
  }
}

async function onSettingsExportBackup(): Promise<void> {
  if (!currentFolder) return;
  setStatus("settings-export-status", "Building backup…");
  try {
    const backup = {
      _format: "personal-finance-web-backup-v1",
      _exported_at: new Date().toISOString(),
      folder_name: currentFolder.name,
      categories,
      cache,
      months: bundle.months.map((m) => ({
        month: m.month,
        transactions: m.transactions,
        files_seen: m.files_seen,
        recon: m.recon,
        summary: m.summary,
      })),
    };
    const json = JSON.stringify(backup, null, 2);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `pf-backup-${escapeForFilename(currentFolder.name)}-${stamp}.json`;
    downloadText(json, filename, "application/json");
    log.info("settings", "Backup downloaded", {
      bytes: json.length,
      months: bundle.months.length,
      cached_vendors: Object.keys(cache).length,
    });
    setStatus(
      "settings-export-status",
      `Saved ${(json.length / 1024).toFixed(1)} KB · ${bundle.months.length} months · ${Object.keys(cache).length} cached vendors.`,
      "success",
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus("settings-export-status", `Failed: ${msg}`, "error");
    log.error("settings", "Backup export failed", { error: msg });
  }
}

function escapeForFilename(s: string): string {
  return s.replace(/[^\w.\-]/g, "_").slice(0, 60);
}

// ─────────────────────────────────────────────────────────────────────
// Reconciliation — duplicate-pair banner + resolve / delete actions
// ─────────────────────────────────────────────────────────────────────

import type { ReconIssue } from "./reconcile.js";

let reconExpanded = false;

function collectOpenIssues(): Array<{ month: string; issue: ReconIssue }> {
  const out: Array<{ month: string; issue: ReconIssue }> = [];
  for (const m of bundle.months) {
    for (const issue of m.recon?.issues ?? []) {
      if (issue.status === "open") out.push({ month: m.month, issue });
    }
  }
  return out;
}

function findTransactionById(id: string): { tx: Transaction; month: string } | null {
  for (const m of bundle.months) {
    const tx = m.transactions.find((t) => t.id === id);
    if (tx) return { tx, month: m.month };
  }
  return null;
}

function renderReconBanner(): void {
  const open = collectOpenIssues();
  const panel = $("recon-panel");
  if (open.length === 0) {
    hide("recon-banner");
    hide("recon-panel");
    reconExpanded = false;
    return;
  }
  show("recon-banner");
  const summary = $("recon-banner-summary");
  summary.textContent =
    open.length === 1
      ? "1 possible duplicate detected — same date, same amount on a different portal."
      : `${open.length} possible duplicates detected — same date, same amount on different portals.`;
  const toggle = $("recon-banner-toggle") as HTMLButtonElement;
  // Re-wire on every render so we keep the latest event closure.
  toggle.onclick = () => {
    reconExpanded = !reconExpanded;
    renderReconBanner();
  };
  toggle.textContent = reconExpanded ? "Hide" : "Review";
  if (reconExpanded) {
    show("recon-panel");
    panel.innerHTML = "";
    for (const { month, issue } of open) panel.appendChild(renderReconIssue(month, issue));
  } else {
    hide("recon-panel");
  }
}

function renderReconIssue(month: string, issue: ReconIssue): HTMLElement {
  const card = document.createElement("div");
  card.classList.add("recon-issue");
  const head = document.createElement("div");
  head.classList.add("recon-issue-head");
  head.innerHTML = `
    <span class="recon-issue-date">${escapeHtml(issue.date)}</span>
    <span class="recon-issue-amount">${formatAmount(-issue.amount)}</span>
    <span class="recon-issue-portals">${issue.portals.map((p) => escapeHtml(p)).join(" ↔ ")}</span>
  `;
  card.appendChild(head);

  const rows = document.createElement("ul");
  rows.classList.add("recon-issue-rows");
  for (const txId of issue.transaction_ids) {
    const found = findTransactionById(txId);
    if (!found) continue;
    const li = document.createElement("li");
    const desc = document.createElement("span");
    desc.classList.add("recon-tx-desc");
    desc.textContent = `${found.tx.portal} · ${found.tx.account} · ${found.tx.vendor_raw}${found.tx.deleted ? " (deleted)" : ""}`;
    li.appendChild(desc);
    if (!found.tx.deleted) {
      const del = document.createElement("button");
      del.type = "button";
      del.classList.add("link-btn");
      del.textContent = "Delete this one";
      del.addEventListener("click", () => onReconDelete(found.tx.id));
      li.appendChild(del);
    }
    rows.appendChild(li);
  }
  card.appendChild(rows);

  const actions = document.createElement("div");
  actions.classList.add("recon-issue-actions");
  const resolve = document.createElement("button");
  resolve.type = "button";
  resolve.classList.add("link-btn");
  resolve.textContent = "Not a duplicate — keep both";
  resolve.addEventListener("click", () => onReconResolve(month, issue));
  actions.appendChild(resolve);
  card.appendChild(actions);

  return card;
}

async function onReconResolve(month: string, issue: ReconIssue): Promise<void> {
  if (!currentFolder) return;
  const target = bundle.months.find((m) => m.month === month);
  if (!target) return;
  const idx = target.recon.issues.findIndex(
    (i) => i.transaction_ids.slice().sort().join("+") === issue.transaction_ids.slice().sort().join("+"),
  );
  if (idx < 0) return;
  target.recon.issues[idx].status = "resolved";
  try {
    await persistAfterBatch(currentFolder, bundle, cache, new Set([month]));
    log.info("recon", `Resolved issue on ${month}`, { tx_ids: issue.transaction_ids });
  } catch (err) {
    setStatus("processing-status", `Save failed: ${err}`, "error");
    target.recon.issues[idx].status = "open"; // roll back in-memory
    return;
  }
  renderReconBanner();
}

async function onReconDelete(txId: string): Promise<void> {
  if (!currentFolder) return;
  const found = findTransactionById(txId);
  if (!found) return;
  try {
    await deleteTransaction(currentFolder, bundle, cache, txId);
  } catch (err) {
    setStatus("processing-status", `Delete failed: ${err}`, "error");
    return;
  }
  log.info("recon", `Deleted tx via recon panel`, { id: txId });
  renderTransactions(); // also re-renders the banner
}

// ─────────────────────────────────────────────────────────────────────
// SimpleFIN — multi-org connect / pull / manage
// ─────────────────────────────────────────────────────────────────────

/** Human-readable "X hours ago" / "Just now" / "Yesterday" for a Date.
 *  Used by the last-pull stamp under the Pull button. */
function formatRelativeTime(when: Date, now: Date = new Date()): string {
  const ms = now.getTime() - when.getTime();
  if (ms < 0) return "in the future";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** Map a list of items to the staleness summary shown under the Pull
 *  button: most-recent last_sync wins. Returns null when no item has
 *  ever been pulled (caller hides the line). */
function summarizeLastSync(items: SimpleFinItem[]): { when: Date; text: string } | null {
  let mostRecent: Date | null = null;
  for (const it of items) {
    if (!it.last_sync) continue;
    const d = new Date(it.last_sync);
    if (Number.isNaN(d.getTime())) continue;
    if (!mostRecent || d.getTime() > mostRecent.getTime()) mostRecent = d;
  }
  if (!mostRecent) return null;
  return { when: mostRecent, text: `Last refreshed ${formatRelativeTime(mostRecent)}` };
}

async function refreshSimplefinButtons(): Promise<void> {
  const items = await getSimplefinItems();
  const lastSyncEl = $("simplefin-last-sync");
  if (items.length === 0) {
    show("simplefin-connect-btn");
    hide("simplefin-pull-btn");
    hide("simplefin-manage-btn");
    setStatus("simplefin-status", "");
    hide("simplefin-last-sync");
    return;
  }
  hide("simplefin-connect-btn");
  show("simplefin-pull-btn");
  show("simplefin-manage-btn");
  const summary = summarizeOrgs(items);
  setStatus("simplefin-status", `Connected to ${summary} via SimpleFIN.`);
  const stamp = summarizeLastSync(items);
  if (stamp) {
    lastSyncEl.textContent = stamp.text;
    lastSyncEl.setAttribute("title", stamp.when.toISOString());
    show("simplefin-last-sync");
  } else {
    hide("simplefin-last-sync");
  }
}

function summarizeOrgs(items: SimpleFinItem[]): string {
  const names = items.map((it) => it.org_name || "SimpleFIN").filter((n) => n.trim());
  if (names.length === 0) return `${items.length} bank${items.length === 1 ? "" : "s"}`;
  if (names.length <= 2) return names.join(" + ");
  return `${names[0]} + ${names.length - 1} more`;
}

async function openSimplefinModal(): Promise<void> {
  // SimpleFIN access URLs land in the vault on connect (post-C19),
  // so we need the vault unlocked before we can save the credentials
  // we're about to receive from the bridge. Prompt up front rather
  // than fail at save time.
  const ok = await ensureUnlocked();
  if (!ok) return;
  ($("sfin-token-input") as HTMLTextAreaElement).value = "";
  setStatus("sfin-connect-status", "");
  show("sfin-modal-backdrop");
  setTimeout(() => ($("sfin-token-input") as HTMLTextAreaElement).focus(), 0);
}

function closeSimplefinModal(): void {
  hide("sfin-modal-backdrop");
}

async function onSimplefinConnectSubmit(): Promise<void> {
  const token = ($("sfin-token-input") as HTMLTextAreaElement).value;
  if (!token.trim()) {
    setStatus("sfin-connect-status", "Paste your setup token first.", "error");
    return;
  }
  setStatus("sfin-connect-status", "Exchanging setup token…");
  ($("sfin-connect-submit") as HTMLButtonElement).disabled = true;
  try {
    const accessUrl = await exchangeSetupToken(token);
    const item = await addSimplefinItem({
      access_url: accessUrl,
      org_name: "",
      last_sync: null,
      accounts: [],
    });
    log.info("simplefin", "Connected — access URL stored in IndexedDB.", { item_id: item.id });
    setStatus("sfin-connect-status", "Connected. Loading your transactions…", "success");
    // Immediate first sync so the user sees data right away.
    await pullSimplefinItem(item.id);
    closeSimplefinModal();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("simplefin", "Connect failed", { error: msg });
    setStatus("sfin-connect-status", msg, "error");
  } finally {
    ($("sfin-connect-submit") as HTMLButtonElement).disabled = false;
  }
}

/** Cross-tab mutex around an aggregator-sync operation. Uses the Web
 *  Locks API (Chrome/Edge 69+, available everywhere we already require
 *  for the File System Access API). Two open tabs hitting Refresh at
 *  the same time would race the cursor + vault writes; with this lock,
 *  the second tab's call short-circuits to "busy" and tells the user
 *  to try again. Concurrent connect-new-bank flows aren't locked —
 *  they don't race the same data.
 *
 *  Falls through to direct invocation when the API is unavailable
 *  (treats absence as best-effort, matches our pre-C21 behaviour). */
async function withAggregatorLock<T>(
  name: "plaid" | "simplefin",
  fn: () => Promise<T>,
): Promise<T | { busy: true }> {
  const nav = navigator as Navigator & { locks?: LockManager };
  if (!nav.locks) return fn();
  return new Promise<T | { busy: true }>((resolve, reject) => {
    nav.locks!
      .request(`pf:${name}:sync`, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          resolve({ busy: true });
          return;
        }
        try {
          const result = await fn();
          resolve(result);
        } catch (err) {
          reject(err);
        }
      })
      .catch(reject);
  });
}

function isBusyResult<T>(r: T | { busy: true }): r is { busy: true } {
  return typeof r === "object" && r !== null && (r as { busy?: boolean }).busy === true;
}

/** "Pull data" button: pulls every connected item in sequence. Aggregates
 *  status into a single banner so the user sees one summary per click. */
async function onSimplefinPullAll(): Promise<void> {
  // Refresh requires the vault unlocked — access_urls live there now.
  const ok = await ensureUnlocked();
  if (!ok) return;
  const items = await getSimplefinItems();
  if (items.length === 0) {
    setStatus("simplefin-status", "No banks connected.", "error");
    return;
  }
  const result = await withAggregatorLock("simplefin", () => pullSimplefinAll());
  if (isBusyResult(result)) {
    setStatus(
      "simplefin-status",
      "Another tab is already refreshing SimpleFIN — try again in a few seconds.",
      "error",
    );
  }
}

async function pullSimplefinAll(opts: { backgroundOnly?: boolean } = {}): Promise<void> {
  const items = await getSimplefinItems();
  if (items.length === 0) return;
  if (!currentFolder) {
    if (!opts.backgroundOnly) setStatus("simplefin-status", "Pick a data folder first.", "error");
    return;
  }
  ($("simplefin-pull-btn") as HTMLButtonElement).disabled = true;
  setStatus("simplefin-status", `Refreshing ${items.length} bank${items.length === 1 ? "" : "s"}…`);
  let totalTx = 0;
  const allErrors: string[] = [];
  const failedOrgs: string[] = [];
  try {
    for (const it of items) {
      try {
        const r = await pullSimplefinItemInner(it.id, { silent: true });
        totalTx += r.transactionsAdded;
        for (const e of r.errors) allErrors.push(e);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        failedOrgs.push(`${it.org_name || it.id}: ${msg}`);
      }
    }
    const okCount = items.length - failedOrgs.length;
    setStatus(
      "simplefin-status",
      `Refreshed ${okCount} bank${okCount === 1 ? "" : "s"} — ${totalTx} new transaction${totalTx === 1 ? "" : "s"}.`,
      failedOrgs.length > 0 ? "error" : "success",
    );
    renderSimplefinErrors(allErrors, failedOrgs);
    await refreshSimplefinButtons();
  } finally {
    ($("simplefin-pull-btn") as HTMLButtonElement).disabled = false;
  }
}

interface PullInnerResult {
  transactionsAdded: number;
  errors: string[];
}

/** Pull one item end-to-end: fetch → apply overrides → processFiles →
 *  merge into bundle → persist → update item's last_sync + accounts.
 *  When `silent` is true, leaves the workbench status alone so the caller
 *  (pullSimplefinAll) can render its own aggregate status. */
async function pullSimplefinItemInner(
  itemId: string,
  opts: { silent?: boolean; startDateOverride?: Date } = {},
): Promise<PullInnerResult> {
  if (!currentFolder) throw new Error("Pick a data folder first.");
  const items = await getSimplefinItems();
  const item = items.find((it) => it.id === itemId);
  if (!item) throw new Error("Bank no longer connected.");

  // Incremental sync: ask for the user's configured window from now, but
  // overlap with last-sync minus 7 days when we have one. The overlap
  // catches backdated transactions some banks post late without paying
  // for a full window each time.
  const settings = await getSimplefinSettings();
  const windowDays = settings.pull_window_days;
  const now = new Date();
  const fullWindowStart = new Date(now.getTime() - windowDays * 86400 * 1000);
  let startDate: Date;
  if (opts.startDateOverride) {
    startDate = opts.startDateOverride;
  } else if (item.last_sync) {
    const since = new Date(item.last_sync);
    if (Number.isNaN(since.getTime())) {
      startDate = fullWindowStart;
    } else {
      const overlap = new Date(since.getTime() - 7 * 86400 * 1000);
      // max(overlap, fullWindowStart) — never ask for less than the
      // configured floor (otherwise a long-paused sync would miss old data).
      startDate = overlap.getTime() < fullWindowStart.getTime() ? fullWindowStart : overlap;
    }
  } else {
    startDate = fullWindowStart;
  }
  if (!opts.silent) {
    setStatus("simplefin-status", `Refreshing ${item.org_name || "your bank"}…`);
    ($("simplefin-pull-btn") as HTMLButtonElement).disabled = true;
  }
  // Access URL lives in the vault now — getItems populates it when
  // the vault is unlocked. If we got here without one, the user
  // refreshed before unlocking; surface a clear error instead of a
  // confusing TypeError. The pull-all path calls ensureUnlocked
  // upstream so this should be very rare.
  if (!item.access_url) {
    throw new Error(
      `Vault is locked — unlock it to refresh ${item.org_name || "this SimpleFIN bank"}.`,
    );
  }
  const docRaw = await fetchAccounts(item.access_url, { startDate });

  // Refresh the cached account list (preserves user overrides), then
  // apply hidden / display_name / kind overrides for processing.
  const mergedAccounts = mergeAccountList(item, docRaw);
  const itemWithFreshAccounts: SimpleFinItem = { ...item, accounts: mergedAccounts };
  const docTransformed = applyAccountOverrides(docRaw, itemWithFreshAccounts);

  const orgName = (docRaw.accounts[0]?.org?.name ?? docRaw.accounts[0]?.org?.domain ?? item.org_name ?? "").trim();
  const filename = `simplefin_pull_${item.id}_${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  const bytes = new TextEncoder().encode(JSON.stringify(docTransformed));
  const result = await processFiles([{ filename, bytes }], {
    cache, categoriesDoc: categories,
  });
  Object.assign(cache, result.newlyLearned);
  const filesSeen = filesSeenByMonth(result.perFile);
  const touched = mergeIntoBundle(bundle, result.transactions, filesSeen);
  await persistAfterBatch(currentFolder, bundle, cache, touched);

  await updateSimplefinItem(item.id, {
    org_name: orgName,
    last_sync: new Date().toISOString(),
    accounts: mergedAccounts,
  });
  // Account renames from the merged list may change effective labels;
  // refresh the cache before the downstream renders so hidden accounts
  // stay hidden even when SimpleFIN renames them upstream.
  await refreshHiddenSimplefinLabels();

  renderPerFile(result.perFile, []);
  renderFilterBar();
  renderSummary();
  renderTransactions();
  log.info("simplefin", `Pull complete: ${orgName || item.id}`, {
    accounts: docRaw.accounts.length,
    visibleAccounts: docTransformed.accounts.length,
    transactions: result.transactions.length,
    errors: docRaw.errors?.length ?? 0,
  });
  return {
    transactionsAdded: result.transactions.length,
    errors: docRaw.errors ?? [],
  };
}

/** Wrapper for pulling a single item from the manage modal — handles
 *  the disabled state of the workbench Pull button so the user can't
 *  fire two pulls concurrently. */
async function pullSimplefinItem(itemId: string): Promise<void> {
  ($("simplefin-pull-btn") as HTMLButtonElement).disabled = true;
  try {
    const r = await pullSimplefinItemInner(itemId);
    const items = await getSimplefinItems();
    const it = items.find((i) => i.id === itemId);
    setStatus(
      "simplefin-status",
      `Refreshed${it?.org_name ? ` ${it.org_name}` : ""} — ${r.transactionsAdded} new transaction${r.transactionsAdded === 1 ? "" : "s"}.`,
      "success",
    );
    renderSimplefinErrors(r.errors, []);
    await refreshSimplefinButtons();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("simplefin", "Pull failed", { error: msg, item_id: itemId });
    setStatus("simplefin-status", msg, "error");
  } finally {
    ($("simplefin-pull-btn") as HTMLButtonElement).disabled = false;
  }
}

/** Render the partial-failure / per-item-error banner. Hidden when
 *  both lists are empty. Aggregator-reported errors (one bank's session
 *  expired, etc.) and our own per-item exceptions (network failure on
 *  one of the orgs) land in the same banner — both are partial outages
 *  the user should know about. */
function renderSimplefinErrors(docErrors: string[], failedOrgs: string[]): void {
  const banner = $("simplefin-errors");
  if (docErrors.length === 0 && failedOrgs.length === 0) {
    hide("simplefin-errors");
    banner.innerHTML = "";
    return;
  }
  const items: string[] = [];
  for (const e of failedOrgs) {
    items.push(`<li><strong>Refresh failed:</strong> ${escapeHtml(e)}</li>`);
  }
  for (const e of docErrors) {
    items.push(`<li>${escapeHtml(e)}</li>`);
  }
  banner.innerHTML = `<strong>SimpleFIN reported issues with this pull:</strong><ul>${items.join("")}</ul>`;
  show("simplefin-errors");
}

// ── manage modal ───────────────────────────────────────────────────

async function openSimplefinManageModal(): Promise<void> {
  // Modal lets the user pull / disconnect / add — all of those need
  // the vault unlocked to read or write access_urls. Prompt up front
  // so the modal doesn't half-work in locked state.
  const ok = await ensureUnlocked();
  if (!ok) return;
  await refreshManageModal();
  show("sfin-manage-backdrop");
}

function closeSimplefinManageModal(): void {
  hide("sfin-manage-backdrop");
}

async function refreshManageModal(): Promise<void> {
  const [items, settings] = await Promise.all([
    getSimplefinItems(),
    getSimplefinSettings(),
  ]);
  ($("sfin-window-select") as HTMLSelectElement).value = String(settings.pull_window_days);
  ($("sfin-autopull-select") as HTMLSelectElement).value = String(settings.auto_pull_hours);
  setStatus("sfin-settings-status", "");
  renderItemsList(items);
}

function renderItemsList(items: SimpleFinItem[]): void {
  const root = $("sfin-items-list");
  if (items.length === 0) {
    root.innerHTML = `<p class="hint">No banks connected yet.</p>`;
    return;
  }
  root.innerHTML = items.map(renderItemCard).join("");
  // Wire each card's event handlers after innerHTML write.
  for (const item of items) {
    const cardId = `sfin-item-${item.id}`;
    const card = document.getElementById(cardId);
    if (!card) continue;
    card.querySelector(".sfin-pull-one")?.addEventListener("click", () => pullSimplefinItem(item.id));
    card.querySelector(".sfin-remove-one")?.addEventListener("click", () => onRemoveItem(item.id, item.org_name));
    for (const acct of item.accounts) {
      const row = card.querySelector(`[data-acct-id="${cssEscape(acct.id)}"]`);
      if (!row) continue;
      row.querySelector(".sfin-acct-display")
        ?.addEventListener("change", (e) => onAccountFieldChange(item.id, acct.id, "display_name", (e.target as HTMLInputElement).value));
      row.querySelector(".sfin-acct-hidden")
        ?.addEventListener("change", (e) => onAccountFieldChange(item.id, acct.id, "hidden", (e.target as HTMLInputElement).checked));
      row.querySelector(".sfin-acct-kind")
        ?.addEventListener("change", (e) => onAccountFieldChange(item.id, acct.id, "account_kind", (e.target as HTMLSelectElement).value));
    }
  }
}

function renderItemCard(item: SimpleFinItem): string {
  const lastSyncText = item.last_sync
    ? `Last refreshed ${formatRelativeTime(new Date(item.last_sync))}`
    : "Not refreshed yet";
  const lastSyncTitle = item.last_sync ?? "";
  const accountRows = item.accounts.length === 0
    ? `<p class="sfin-empty-accounts">No accounts seen yet — click <strong>Refresh now</strong> below.</p>`
    : `<table class="sfin-accounts-table">
         <thead><tr>
           <th>Account</th>
           <th>Display name</th>
           <th>Kind</th>
           <th>Hidden</th>
         </tr></thead>
         <tbody>${item.accounts.map((a) => renderAccountRow(a)).join("")}</tbody>
       </table>`;
  return `
    <div class="sfin-item-card" id="sfin-item-${escapeHtml(item.id)}">
      <div class="sfin-item-head">
        <div>
          <div class="sfin-item-title">${escapeHtml(item.org_name || "SimpleFIN bank")}</div>
          <div class="sfin-item-meta" title="${escapeHtml(lastSyncTitle)}">${escapeHtml(lastSyncText)}</div>
        </div>
        <div class="sfin-item-actions">
          <button type="button" class="secondary sfin-pull-one">Refresh now</button>
          <button type="button" class="link-btn sfin-remove-one">Disconnect</button>
        </div>
      </div>
      ${accountRows}
    </div>
  `;
}

function renderAccountRow(a: { id: string; name: string; currency: string; display_name?: string; hidden?: boolean; account_kind?: "debit" | "liability" }): string {
  return `
    <tr data-acct-id="${escapeHtml(a.id)}">
      <td>
        <div>${escapeHtml(a.name)}</div>
        ${a.currency ? `<div class="sfin-item-meta">${escapeHtml(a.currency)}</div>` : ""}
      </td>
      <td>
        <input type="text" class="sfin-acct-display"
          value="${escapeHtml(a.display_name ?? "")}"
          placeholder="${escapeHtml(a.name)}" />
      </td>
      <td>
        <select class="sfin-acct-kind">
          <option value=""${a.account_kind ? "" : " selected"}>Auto (debit)</option>
          <option value="debit"${a.account_kind === "debit" ? " selected" : ""}>Debit</option>
          <option value="liability"${a.account_kind === "liability" ? " selected" : ""}>Liability</option>
        </select>
      </td>
      <td>
        <input type="checkbox" class="sfin-acct-hidden" ${a.hidden ? "checked" : ""} />
      </td>
    </tr>
  `;
}

// CSS.escape isn't typed under DOM lib in some setups; minimal escape for
// the data-acct-id selector. SimpleFIN account ids are typically opaque
// hex/ascii but we don't want to assume that.
function cssEscape(s: string): string {
  if (typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS?.escape === "function") {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

async function onAccountFieldChange(
  itemId: string,
  acctId: string,
  field: "display_name" | "hidden" | "account_kind",
  value: string | boolean,
): Promise<void> {
  const items = await getSimplefinItems();
  const item = items.find((it) => it.id === itemId);
  if (!item) return;
  const accounts = item.accounts.map((a) => {
    if (a.id !== acctId) return a;
    const next = { ...a };
    if (field === "display_name") {
      const v = typeof value === "string" ? value.trim() : "";
      if (v) next.display_name = v;
      else delete next.display_name;
    } else if (field === "hidden") {
      if (value) next.hidden = true;
      else delete next.hidden;
    } else if (field === "account_kind") {
      const v = typeof value === "string" ? value : "";
      if (v === "debit" || v === "liability") next.account_kind = v;
      else delete next.account_kind;
    }
    return next;
  });
  await updateSimplefinItem(itemId, { accounts });
  log.info("simplefin", "Account override updated", { itemId, acctId, field });
  // Toggling `hidden` (and renames that affect labels) needs to take
  // effect on already-imported rows, not just future pulls. Refresh the
  // cached label set and re-render the filter / summary / table so the
  // change is immediately visible.
  if (field === "hidden" || field === "display_name") {
    await refreshHiddenSimplefinLabels();
    renderFilterBar();
    renderSummary();
    renderTransactions();
  }
}

async function onRemoveItem(itemId: string, orgName: string): Promise<void> {
  const ok = confirm(
    `Remove ${orgName || "this bank"}? Already-imported transactions stay in your data folder; only the connection credentials and per-account overrides are removed.`,
  );
  if (!ok) return;
  await removeSimplefinItem(itemId);
  log.info("simplefin", "Bank removed", { itemId });
  // Removing the item also drops its hidden-account overrides, so any
  // rows that were hidden retroactively now reappear in the view.
  await refreshHiddenSimplefinLabels();
  await refreshManageModal();
  await refreshSimplefinButtons();
  renderFilterBar();
  renderSummary();
  renderTransactions();
}

async function onSimplefinSettingsChange(): Promise<void> {
  const pull_window_days = Number(($("sfin-window-select") as HTMLSelectElement).value);
  const auto_pull_hours = Number(($("sfin-autopull-select") as HTMLSelectElement).value);
  await saveSimplefinSettings({ pull_window_days, auto_pull_hours });
  setStatus("sfin-settings-status", "Saved.", "success");
  // Clear the saved-confirmation a beat later so the modal doesn't shout.
  setTimeout(() => setStatus("sfin-settings-status", ""), 1500);
}

/** Background auto-pull on app open. Fires only when (a) at least one
 *  item is connected, (b) auto-pull is enabled, (c) the most-recent
 *  last_sync is older than the configured threshold (or never). Runs
 *  silently — no UI disruption except the eventual "pulled N txs"
 *  status update. */
async function maybeAutoPull(): Promise<void> {
  const [items, settings] = await Promise.all([
    getSimplefinItems(),
    getSimplefinSettings(),
  ]);
  if (items.length === 0) return;
  if (!settings.auto_pull_hours || settings.auto_pull_hours <= 0) return;
  const threshold = settings.auto_pull_hours * 3600 * 1000;
  const now = Date.now();
  const anyStale = items.some((it) => {
    if (!it.last_sync) return true;
    const t = new Date(it.last_sync).getTime();
    if (Number.isNaN(t)) return true;
    return now - t > threshold;
  });
  if (!anyStale) return;
  // Auto-pull is supposed to be silent. With access_urls in the vault
  // now, a stale-pull on a locked vault would have to prompt for a
  // passphrase — which would be intrusive and a poor first-run
  // experience. Skip the auto-pull entirely while locked; the next
  // user-initiated Refresh will handle it after they unlock.
  if (!vault.isUnlocked()) {
    log.info("simplefin", "Auto-pull skipped — vault locked");
    return;
  }
  log.info("simplefin", "Auto-pulling stale items on app open", {
    items: items.length,
    threshold_hours: settings.auto_pull_hours,
  });
  await pullSimplefinAll({ backgroundOnly: true });
}

// ─────────────────────────────────────────────────────────────────────
// Plaid — connect / pull / manage banks
// ─────────────────────────────────────────────────────────────────────
//
// Visibility rules for the workbench buttons:
//   - No vault yet: show "Connect to Plaid" so the user can start.
//     (Clicking it pops the vault set-passphrase modal first.)
//   - Vault unlocked, no creds saved: same — "Connect to Plaid" starts
//     the setup flow.
//   - Vault unlocked, creds saved, no items: "Connect to Plaid" still
//     visible (re-uses creds, opens Plaid Link directly).
//   - Vault unlocked, creds saved, ≥1 item: "Pull data from Plaid" +
//     "Manage banks" visible; "Connect" hidden.
//
// We can only know "creds saved" + "items count" with an unlocked
// vault. Without unlocking, fall back to a generic "Connect to Plaid".

async function refreshPlaidButtons(): Promise<void> {
  if (!vault.isUnlocked()) {
    // Vault locked — we can't introspect storage. Show the entry-point
    // button; clicking it triggers ensureUnlocked() first.
    show("plaid-connect-btn");
    hide("plaid-pull-btn");
    hide("plaid-manage-btn");
    setStatus("plaid-status", "");
    return;
  }
  const hasItems = await hasAnyPlaidItem();
  if (hasItems) {
    hide("plaid-connect-btn");
    show("plaid-pull-btn");
    show("plaid-manage-btn");
    const items = await listPlaidItems();
    const tally = items.length === 1
      ? `Connected to ${items[0].institution_name} via Plaid.`
      : `Connected to ${items.length} banks via Plaid.`;
    setStatus("plaid-status", tally);
  } else {
    show("plaid-connect-btn");
    hide("plaid-pull-btn");
    hide("plaid-manage-btn");
    setStatus("plaid-status", "");
  }
}

// ── credentials modal ─────────────────────────────────────────────

let pendingCredsAction: "connect" | "edit" = "connect";

async function openPlaidCredsModal(action: "connect" | "edit"): Promise<void> {
  pendingCredsAction = action;
  ($("plaid-cred-client-id") as HTMLInputElement).value = "";
  ($("plaid-cred-secret") as HTMLInputElement).value = "";
  ($("plaid-cred-env") as HTMLSelectElement).value = "production";
  setStatus("plaid-creds-status", "");
  // Show the exact URL the user needs to register in their Plaid
  // dashboard for OAuth banks to work. Pulled from window.location so
  // it always matches whatever URL they're currently on.
  const redirectUri = computeRedirectUri();
  const redirectChip = document.getElementById("plaid-redirect-uri-display");
  if (redirectChip && redirectUri) redirectChip.textContent = redirectUri;
  let initialCountries: ReadonlyArray<string> = DEFAULT_PLAID_COUNTRIES;
  if (action === "edit") {
    const existing = await getPlaidCreds();
    if (existing) {
      ($("plaid-cred-client-id") as HTMLInputElement).value = existing.client_id;
      ($("plaid-cred-secret") as HTMLInputElement).value = existing.secret;
      ($("plaid-cred-env") as HTMLSelectElement).value = existing.env;
      if (existing.country_codes && existing.country_codes.length > 0) {
        initialCountries = existing.country_codes;
      }
    }
    ($("plaid-creds-submit") as HTMLButtonElement).textContent = "Save changes";
    ($("plaid-creds-modal-title") as HTMLElement).textContent = "Edit your Plaid API keys";
  } else {
    ($("plaid-creds-submit") as HTMLButtonElement).textContent = "Save and continue";
    ($("plaid-creds-modal-title") as HTMLElement).textContent = "Connect a bank (via Plaid)";
  }
  renderPlaidCountryCheckboxes(initialCountries);
  show("plaid-creds-modal-backdrop");
  setTimeout(() => ($("plaid-cred-client-id") as HTMLInputElement).focus(), 0);
}

/** Build the checkbox list inside the "Bank countries" disclosure +
 *  update the summary chip. Called on every modal open so the state
 *  always reflects the current saved creds (or defaults). */
function renderPlaidCountryCheckboxes(selected: ReadonlyArray<string>): void {
  const list = document.getElementById("plaid-countries-list");
  if (!list) return;
  list.innerHTML = "";
  const selectedSet = new Set(selected.map((c) => c.toUpperCase()));
  for (const c of PLAID_TRANSACTIONS_COUNTRIES) {
    const label = document.createElement("label");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.value = c.code;
    cb.checked = selectedSet.has(c.code);
    cb.addEventListener("change", () => updatePlaidCountrySummary());
    const span = document.createElement("span");
    span.textContent = `${c.code} · ${c.name}`;
    label.appendChild(cb);
    label.appendChild(span);
    list.appendChild(label);
  }
  updatePlaidCountrySummary();
}

function readSelectedPlaidCountries(): string[] {
  const list = document.getElementById("plaid-countries-list");
  if (!list) return [...DEFAULT_PLAID_COUNTRIES];
  const out: string[] = [];
  list.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked").forEach((cb) => {
    out.push(cb.value);
  });
  return out;
}

function updatePlaidCountrySummary(): void {
  const summary = document.getElementById("plaid-countries-summary");
  if (!summary) return;
  const codes = readSelectedPlaidCountries();
  const names = codes
    .map((code) => PLAID_TRANSACTIONS_COUNTRIES.find((c) => c.code === code)?.name ?? code)
    .filter(Boolean);
  if (names.length === 0) summary.textContent = "(none — defaults to US, Canada)";
  else if (names.length <= 3) summary.textContent = names.join(", ");
  else summary.textContent = `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
}

function closePlaidCredsModal(): void {
  hide("plaid-creds-modal-backdrop");
  // Don't leave the secret sitting in the DOM after close.
  ($("plaid-cred-secret") as HTMLInputElement).value = "";
}

async function onPlaidCredsSubmit(): Promise<void> {
  const client_id = ($("plaid-cred-client-id") as HTMLInputElement).value.trim();
  const secret = ($("plaid-cred-secret") as HTMLInputElement).value.trim();
  const env = ($("plaid-cred-env") as HTMLSelectElement).value as PlaidEnv;
  if (!client_id || !secret) {
    setStatus("plaid-creds-status", "Both client_id and secret are required.", "error");
    return;
  }
  const country_codes = readSelectedPlaidCountries();
  if (country_codes.length === 0) {
    setStatus(
      "plaid-creds-status",
      "Pick at least one bank country. Default is US + Canada.",
      "error",
    );
    return;
  }
  const submit = $("plaid-creds-submit") as HTMLButtonElement;
  submit.disabled = true;
  setStatus("plaid-creds-status", "Saving…");
  try {
    await savePlaidCreds({ client_id, secret, env, country_codes });
    log.info("plaid", "Plaid credentials saved (encrypted in vault).", {
      country_codes,
    });
    closePlaidCredsModal();
    if (pendingCredsAction === "connect") {
      await startPlaidConnectFlow({ client_id, secret, env, country_codes });
    } else {
      setStatus("plaid-status", "API keys updated.", "success");
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("plaid", "Save credentials failed", { error: msg });
    setStatus("plaid-creds-status", msg, "error");
  } finally {
    submit.disabled = false;
  }
}

// ── account picker (returns a Promise) ────────────────────────────

let pendingAccountPick: {
  accounts: KnownAccount[];
  resolve: (selected: string[]) => void;
  reject: (err: Error) => void;
} | null = null;

function openPlaidAccountsModal(institutionName: string, accounts: KnownAccount[]): Promise<string[]> {
  if (pendingAccountPick) {
    return Promise.reject(new Error("Another account picker is already open"));
  }
  // Pre-select everything by default — most users will want all
  // accounts at a freshly-linked bank, and unchecking is cheaper UX
  // than searching for the right ones to check.
  ($("plaid-accounts-institution") as HTMLElement).textContent = institutionName;
  const list = $("plaid-accounts-list") as HTMLUListElement;
  list.innerHTML = "";
  for (const acc of accounts) {
    const li = document.createElement("li");
    const label = document.createElement("label");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.value = acc.account_id;
    cb.checked = true;
    label.appendChild(cb);
    const name = document.createElement("span");
    name.textContent = acc.name || acc.account_id;
    label.appendChild(name);
    const meta = document.createElement("span");
    meta.className = "account-meta";
    const masked = acc.mask ? `····${acc.mask}` : "";
    const kind = acc.subtype || acc.type || "";
    meta.textContent = [masked, kind].filter(Boolean).join(" · ");
    label.appendChild(meta);
    li.appendChild(label);
    list.appendChild(li);
  }
  setStatus("plaid-accounts-status", "");
  show("plaid-accounts-modal-backdrop");
  return new Promise<string[]>((resolve, reject) => {
    pendingAccountPick = { accounts, resolve, reject };
  });
}

function onPlaidAccountsClose(submit: boolean): void {
  if (!pendingAccountPick) {
    hide("plaid-accounts-modal-backdrop");
    return;
  }
  if (!submit) {
    const p = pendingAccountPick;
    pendingAccountPick = null;
    hide("plaid-accounts-modal-backdrop");
    p.reject(new Error("Account selection cancelled."));
    return;
  }
  const list = $("plaid-accounts-list") as HTMLUListElement;
  const selected: string[] = [];
  list.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked").forEach((cb) => {
    selected.push(cb.value);
  });
  if (selected.length === 0) {
    setStatus("plaid-accounts-status", "Pick at least one account.", "error");
    return;
  }
  const p = pendingAccountPick;
  pendingAccountPick = null;
  hide("plaid-accounts-modal-backdrop");
  p.resolve(selected);
}

const accountPickerImpl: AccountPicker = {
  pickForNewItem: async ({ institutionName, accounts }) => {
    const selected = await openPlaidAccountsModal(institutionName, accounts);
    return { selected };
  },
};

// ── connect flow ──────────────────────────────────────────────────

async function onPlaidConnect(): Promise<void> {
  const ok = await ensureUnlocked();
  if (!ok) return;
  const existingCreds = await getPlaidCreds();
  if (!existingCreds) {
    await openPlaidCredsModal("connect");
    return;
  }
  await startPlaidConnectFlow(existingCreds);
}

// Detect + finish an OAuth-redirect resume on app boot. The Plaid
// orchestrator handles the detection (?oauth_state_id in URL +
// matching sessionStorage entry); we just plumb the post-link flow.
// No-op when the URL isn't an OAuth return — main code path is
// unaffected.
async function maybeResumeOAuthLink(): Promise<void> {
  if (
    typeof window === "undefined" ||
    !new URLSearchParams(window.location.search).get("oauth_state_id")
  ) {
    return;
  }
  // Detected an OAuth return. Need vault unlocked + creds present.
  const ok = await ensureUnlocked();
  if (!ok) return;
  const creds = await getPlaidCreds();
  if (!creds) {
    log.warn("plaid", "OAuth return on boot but no Plaid creds saved — ignoring");
    return;
  }
  setStatus("plaid-status", "Finishing bank login…");
  try {
    const item = await resumeOAuthLinkIfPending(creds, accountPickerImpl);
    if (!item) return; // session expired / wasn't actually pending
    log.info("plaid", "OAuth Link resume completed", {
      institution: item.institution_name,
      accounts_selected: item.selected_account_ids?.length ?? 0,
    });
    setStatus("plaid-status", `Linked ${item.institution_name}. Loading your transactions…`, "success");
    await pullPlaidForItem(creds, item);
    await refreshPlaidButtons();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/cancelled|closed without/i.test(msg)) {
      setStatus("plaid-status", "");
      log.info("plaid", "OAuth resume cancelled");
    } else {
      setStatus("plaid-status", msg, "error");
      log.error("plaid", "OAuth resume failed", { error: msg });
    }
  }
}

async function startPlaidConnectFlow(creds: PlaidCredentials): Promise<void> {
  setStatus("plaid-status", "Opening your bank login…");
  try {
    const item = await connectNewBank(creds, accountPickerImpl);
    log.info("plaid", "New bank linked", {
      institution: item.institution_name,
      accounts_selected: item.selected_account_ids?.length ?? 0,
    });
    setStatus("plaid-status", `Linked ${item.institution_name}. Loading your transactions…`, "success");
    // Kick off the first sync immediately so the user sees data right away.
    await pullPlaidForItem(creds, item);
    await refreshPlaidButtons();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/cancelled|closed without/i.test(msg)) {
      setStatus("plaid-status", "");
      log.info("plaid", "Connect cancelled by user");
    } else {
      setStatus("plaid-status", msg, "error");
      log.error("plaid", "Connect failed", { error: msg });
    }
  }
}

// Single-item sync used right after connect. For the "Pull from Plaid"
// button on the workbench we run all items via pullAllItems (below).
async function pullPlaidForItem(creds: PlaidCredentials, item: PlaidItem): Promise<void> {
  if (!currentFolder) {
    setStatus("plaid-status", "Pick a data folder first.", "error");
    return;
  }
  // Share the cross-tab Plaid lock with onPlaidPull / onPlaidResetAll
  // so a single-item sync (post-connect or post-relink) can't race
  // a pull-all running in another tab. Busy → fail soft with a hint.
  const result = await withAggregatorLock("plaid", async () => {
    try {
      const r = await pullPlaidSync(creds, item);
      await acceptPlaidPullResult(item, r);
      return { ok: true } as const;
    } catch (err) {
      return { ok: false as const, err };
    }
  });
  if (isBusyResult(result)) {
    setStatus(
      "plaid-status",
      "Another tab is already syncing this bank — try again in a few seconds.",
      "error",
    );
    return;
  }
  if (result.ok) return;
  const err = result.err;
  const msg = err instanceof Error ? err.message : String(err);
  log.error("plaid", "First sync failed", { item: item.institution_name, error: msg });
  // If the bank invalidated our session (ITEM_LOGIN_REQUIRED), flag
  // this item so Manage Banks shows a "Re-link" button. The pull-
  // all path does this inside the orchestrator.
  const wasRelinkable = await maybeFlagRelink(item.item_id, err);
  if (wasRelinkable) {
    setStatus(
      "plaid-status",
      `${item.institution_name} needs to be re-linked (bank password changed?). Open Manage banks to fix.`,
      "error",
    );
  } else {
    setStatus("plaid-status", `Linked, but couldn't load transactions yet: ${msg}`, "error");
  }
  await refreshPlaidButtons();
}

// Hand a sync result to the existing processFiles pipeline. Mirrors
// the SimpleFIN pull path — same file-shape, same dedup/cache flow.
async function acceptPlaidPullResult(item: PlaidItem, result: PlaidSyncResult): Promise<void> {
  if (!currentFolder) return;
  const bytes = new TextEncoder().encode(result.jsonText);
  const procResult = await processFiles([{ filename: result.filename, bytes }], {
    cache,
    categoriesDoc: categories,
  });
  Object.assign(cache, procResult.newlyLearned);
  const filesSeen = filesSeenByMonth(procResult.perFile);
  const touched = mergeIntoBundle(bundle, procResult.transactions, filesSeen);
  // Apply any Plaid removed[] from this sync — flips matching txs to
  // deleted = true so reversed pending charges and bank corrections
  // disappear from the dashboard.
  if (procResult.removedPlaidIds.size > 0) {
    const removedTouched = applyPlaidRemoved(bundle, procResult.removedPlaidIds);
    for (const m of removedTouched) touched.add(m);
    log.info("plaid", "Applied Plaid removed[] from sync", {
      item: item.institution_name,
      removedSeen: procResult.removedPlaidIds.size,
      monthsTouched: removedTouched.size,
    });
  }
  await persistAfterBatch(currentFolder, bundle, cache, touched);
  await updatePlaidItemAfterSync(item.item_id, result.finalCursor, new Date());
  renderPerFile(procResult.perFile, []);
  renderFilterBar();
  renderSummary();
  renderTransactions();
  if (result.hadWarmUp) {
    log.info("plaid", "First sync warmed up (PRODUCT_NOT_READY retries)", {
      item: item.institution_name,
    });
  }
  if (result.filteredOutCount > 0) {
    log.info("plaid", "Skipped transactions from non-selected accounts", {
      item: item.institution_name,
      filtered_out: result.filteredOutCount,
    });
  }
}

// ── pull all ──────────────────────────────────────────────────────

async function onPlaidPull(): Promise<void> {
  const ok = await ensureUnlocked();
  if (!ok) return;
  const creds = await getPlaidCreds();
  if (!creds) {
    setStatus("plaid-status", "Plaid API keys not saved yet — connect a bank first.", "error");
    return;
  }
  if (!currentFolder) {
    setStatus("plaid-status", "Pick a data folder first.", "error");
    return;
  }
  const btn = $("plaid-pull-btn") as HTMLButtonElement;
  btn.disabled = true;
  setStatus("plaid-status", "Refreshing your Plaid banks…");
  try {
    const result = await withAggregatorLock("plaid", () =>
      pullAllItems(creds, {
        acceptResult: async (item, syncResult) => {
          await acceptPlaidPullResult(item, syncResult);
        },
        reportItemError: (item, err) => {
          const msg = err instanceof PlaidApiError ? err.message : String(err);
          log.error("plaid", "Item sync failed", {
            item: item.institution_name,
            error: msg,
          });
        },
      }),
    );
    if (isBusyResult(result)) {
      setStatus(
        "plaid-status",
        "Another tab is already refreshing — try again in a few seconds.",
        "error",
      );
      return;
    }
    const out = result;
    const summary =
      out.failedCount === 0
        ? `Refreshed ${out.successCount} bank${out.successCount === 1 ? "" : "s"}.`
        : `Refreshed ${out.successCount}, ${out.failedCount} failed — see Activity log.`;
    setStatus("plaid-status", summary, out.failedCount === 0 ? "success" : "error");
    log.info("plaid", "Pull-all complete", out);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus("plaid-status", msg, "error");
    log.error("plaid", "Pull-all failed", { error: msg });
  } finally {
    btn.disabled = false;
  }
}

// ── manage modal ──────────────────────────────────────────────────

async function openPlaidManageModal(): Promise<void> {
  const ok = await ensureUnlocked();
  if (!ok) return;
  setStatus("plaid-manage-status", "");
  await renderPlaidManageList();
  show("plaid-manage-modal-backdrop");
}

function closePlaidManageModal(): void {
  hide("plaid-manage-modal-backdrop");
}

async function renderPlaidManageList(): Promise<void> {
  const items = await listPlaidItems();
  const list = $("plaid-manage-list") as HTMLUListElement;
  list.innerHTML = "";
  if (items.length === 0) {
    const empty = document.createElement("li");
    empty.textContent = "No banks linked yet. Click '+ Add another bank' above.";
    empty.style.color = "var(--muted)";
    list.appendChild(empty);
    return;
  }
  for (const item of items) {
    list.appendChild(renderPlaidItemRow(item));
  }
}

function renderPlaidItemRow(item: PlaidItem): HTMLLIElement {
  const li = document.createElement("li");
  li.dataset.itemId = item.item_id;
  if (item.needs_relink) li.classList.add("item-needs-relink");

  const row = document.createElement("div");
  row.className = "item-row";
  const name = document.createElement("span");
  name.className = "item-name";
  name.textContent = item.institution_name;
  row.appendChild(name);
  const meta = document.createElement("span");
  meta.className = "item-meta";
  const synced = item.last_sync ? new Date(item.last_sync).toLocaleString() : "never";
  const selCount = item.selected_account_ids?.length ?? "all";
  meta.textContent = `Last sync: ${synced} · ${selCount} account(s)`;
  row.appendChild(meta);

  const actions = document.createElement("span");
  actions.className = "item-actions";

  // Re-link required: show a prominent button instead of the routine
  // actions. The user can't usefully sync this item until they
  // re-authenticate, so demote the other buttons.
  if (item.needs_relink) {
    const warn = document.createElement("div");
    warn.className = "item-relink-warning";
    warn.textContent =
      "⚠ Re-link required — your bank password may have changed. Click below to refresh the connection.";
    li.appendChild(warn);

    const relinkBtn = document.createElement("button");
    relinkBtn.type = "button";
    relinkBtn.className = "secondary";
    relinkBtn.textContent = "Re-link bank";
    relinkBtn.title =
      "Open Plaid Link to re-authenticate with your bank. The connection survives — only the bank login is refreshed.";
    relinkBtn.addEventListener("click", () => onPlaidRelinkItem(item));
    actions.appendChild(relinkBtn);
  } else {
    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "link-btn";
    editBtn.textContent = "Edit accounts";
    editBtn.addEventListener("click", () => togglePlaidEditPanel(li, item));
    actions.appendChild(editBtn);
    const resetBtn = document.createElement("button");
    resetBtn.type = "button";
    resetBtn.className = "link-btn";
    resetBtn.textContent = "↻ Reload full history";
    resetBtn.title = "Re-fetch every transaction this bank has on file (up to 24 months). Already-imported rows stay deduped.";
    resetBtn.addEventListener("click", () => onPlaidResetItem(item));
    actions.appendChild(resetBtn);
  }

  const disconnectBtn = document.createElement("button");
  disconnectBtn.type = "button";
  disconnectBtn.className = "link-btn";
  disconnectBtn.textContent = "Disconnect";
  disconnectBtn.addEventListener("click", () => onPlaidDisconnectItem(item));
  actions.appendChild(disconnectBtn);
  row.appendChild(actions);
  li.appendChild(row);
  return li;
}

function togglePlaidEditPanel(li: HTMLLIElement, item: PlaidItem): void {
  const existing = li.querySelector(".edit-accounts-panel");
  if (existing) {
    existing.remove();
    return;
  }
  const panel = document.createElement("div");
  panel.className = "edit-accounts-panel";
  const known = item.known_accounts ?? [];
  if (known.length === 0) {
    panel.textContent = "No account snapshot yet — try syncing this bank first to populate the list.";
  } else {
    const ul = document.createElement("ul");
    const selected = new Set(item.selected_account_ids ?? known.map((a) => a.account_id));
    for (const acc of known) {
      const itemLi = document.createElement("li");
      const label = document.createElement("label");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.value = acc.account_id;
      cb.checked = selected.has(acc.account_id);
      label.appendChild(cb);
      const text = document.createElement("span");
      const masked = acc.mask ? ` ····${acc.mask}` : "";
      const kind = acc.subtype || acc.type ? ` · ${acc.subtype || acc.type}` : "";
      text.textContent = `${acc.name || acc.account_id}${masked}${kind}`;
      label.appendChild(text);
      itemLi.appendChild(label);
      ul.appendChild(itemLi);
    }
    panel.appendChild(ul);
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "primary";
    saveBtn.textContent = "Save selection";
    saveBtn.addEventListener("click", async () => {
      const chosen: string[] = [];
      ul.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked").forEach((cb) => chosen.push(cb.value));
      if (chosen.length === 0) {
        setStatus("plaid-manage-status", "Pick at least one account.", "error");
        return;
      }
      saveBtn.disabled = true;
      try {
        await updateItemSelection(item.item_id, chosen);
        log.info("plaid", "Selection updated", { item: item.institution_name, accounts: chosen.length });
        setStatus("plaid-manage-status", "Saved.", "success");
        await renderPlaidManageList();
      } catch (err) {
        setStatus("plaid-manage-status", String(err), "error");
      } finally {
        saveBtn.disabled = false;
      }
    });
    panel.appendChild(saveBtn);
  }
  li.appendChild(panel);
}

async function onPlaidDisconnectItem(item: PlaidItem): Promise<void> {
  const ok = window.confirm(
    `Disconnect ${item.institution_name}? Already-imported transactions stay in your data folder; this only removes the connection. You can re-link later.`,
  );
  if (!ok) return;
  setStatus("plaid-manage-status", "Disconnecting…");
  // Best-effort /item/remove against Plaid. If it fails (network,
  // revoked credentials, etc.) we still drop the local vault entry —
  // the access_token is single-bank scoped, so a stale one on Plaid's
  // side without our copy is harmless to the user.
  try {
    const creds = await getPlaidCreds();
    if (creds) await itemRemove(creds, item.access_token);
  } catch (err) {
    log.warn("plaid", "Plaid /item/remove failed (continuing local cleanup)", {
      item: item.institution_name,
      error: String(err),
    });
  }
  await deletePlaidItem(item.item_id);
  log.info("plaid", "Item disconnected", { item: item.institution_name });
  await renderPlaidManageList();
  await refreshPlaidButtons();
  // If no items left, also clear credentials so a fresh setup starts clean.
  if (!(await hasAnyPlaidItem())) {
    setStatus("plaid-manage-status", "All banks disconnected.", "success");
  } else {
    setStatus("plaid-manage-status", "Disconnected.", "success");
  }
}

async function onPlaidAddAnother(): Promise<void> {
  const creds = await getPlaidCreds();
  if (!creds) {
    closePlaidManageModal();
    await openPlaidCredsModal("connect");
    return;
  }
  closePlaidManageModal();
  await startPlaidConnectFlow(creds);
}

async function onPlaidEditCreds(): Promise<void> {
  closePlaidManageModal();
  await openPlaidCredsModal("edit");
}

// ── reset & re-pull ───────────────────────────────────────────────
//
// Clearing an item's last_cursor causes the next /transactions/sync
// to behave like a fresh sync: Plaid re-emits every transaction it
// still has for the item (default 24-month window). processFiles()
// dedupes by hash id so the dashboard doesn't grow duplicates —
// existing rows just get re-touched. The user-facing effect is
// "catch up on old transactions I never imported."

// Re-link an Item whose access_token Plaid invalidated (typically
// after the user changed their bank password). Runs Plaid Link in
// update mode: same item_id, same access_token, just a fresh login.
async function onPlaidRelinkItem(item: PlaidItem): Promise<void> {
  const ok = await ensureUnlocked();
  if (!ok) return;
  const creds = await getPlaidCreds();
  if (!creds) {
    setStatus("plaid-manage-status", "Plaid API keys missing — re-enter them first.", "error");
    return;
  }
  closePlaidManageModal();
  setStatus("plaid-status", `Re-linking ${item.institution_name}…`);
  try {
    await reLinkItem(creds, item);
    log.info("plaid", "Re-link succeeded", { item: item.institution_name });
    setStatus("plaid-status", `Re-linked ${item.institution_name}. Refreshing transactions…`, "success");
    // Kick off a fresh sync now that the bank session is valid again.
    // The item's needs_relink flag was cleared by reLinkItem; if the
    // sync fails again with ITEM_LOGIN_REQUIRED, pullPlaidForItem will
    // re-flag it.
    const refreshed = (await listPlaidItems()).find((i) => i.item_id === item.item_id);
    if (refreshed) await pullPlaidForItem(creds, refreshed);
    await refreshPlaidButtons();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/closed before|closed without|did not complete/i.test(msg)) {
      setStatus("plaid-status", "", "info");
      log.info("plaid", "Re-link cancelled by user", { item: item.institution_name });
    } else {
      log.error("plaid", "Re-link failed", { item: item.institution_name, error: msg });
      setStatus("plaid-status", msg, "error");
    }
  }
}

async function onPlaidResetItem(item: PlaidItem): Promise<void> {
  const ok = window.confirm(
    `Reload full history for ${item.institution_name}?\n\n` +
      "This re-fetches every transaction your bank has on file (up to " +
      "24 months). Transactions you've already imported won't be duplicated " +
      "— they just get re-checked.\n\nContinue?",
  );
  if (!ok) return;
  closePlaidManageModal();
  setStatus("plaid-status", `Reloading history for ${item.institution_name}…`);
  try {
    const reset = await resetItemCursor(item.item_id);
    log.info("plaid", "Cursor reset", { item: item.institution_name });
    const creds = await getPlaidCreds();
    if (!creds) {
      setStatus("plaid-status", "Plaid API keys missing — re-enter them from Manage banks.", "error");
      return;
    }
    await pullPlaidForItem(creds, reset);
    setStatus("plaid-status", `Reloaded history for ${item.institution_name}.`, "success");
    await refreshPlaidButtons();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log.error("plaid", "Reload full history failed", { item: item.institution_name, error: msg });
    setStatus("plaid-status", msg, "error");
  }
}

async function onPlaidResetAll(): Promise<void> {
  const items = await listPlaidItems();
  if (items.length === 0) {
    setStatus("plaid-manage-status", "No banks linked.", "error");
    return;
  }
  const ok = window.confirm(
    `Reload full history for all ${items.length} bank${items.length === 1 ? "" : "s"}?\n\n` +
      "This re-fetches every transaction each linked bank has on file " +
      "(up to 24 months per bank). Already-imported transactions stay " +
      "deduped — nothing duplicates.\n\nContinue?",
  );
  if (!ok) return;
  closePlaidManageModal();
  setStatus("plaid-status", `Reloading history for ${items.length} bank${items.length === 1 ? "" : "s"}…`);
  const btn = $("plaid-pull-btn") as HTMLButtonElement;
  btn.disabled = true;
  try {
    const creds = await getPlaidCreds();
    if (!creds) {
      setStatus("plaid-status", "Plaid API keys not saved yet — connect a bank first.", "error");
      return;
    }
    const result = await withAggregatorLock("plaid", async () => {
      const n = await resetAllCursors();
      log.info("plaid", "All cursors reset", { count: n });
      return pullAllItems(creds, {
        acceptResult: async (item, syncResult) => acceptPlaidPullResult(item, syncResult),
        reportItemError: (item, err) => {
          const msg = err instanceof PlaidApiError ? err.message : String(err);
          log.error("plaid", "Item re-pull failed", { item: item.institution_name, error: msg });
        },
      });
    });
    if (isBusyResult(result)) {
      setStatus(
        "plaid-status",
        "Another tab is already syncing — try again in a few seconds.",
        "error",
      );
      return;
    }
    const out = result;
    const summary =
      out.failedCount === 0
        ? `Reloaded full history for ${out.successCount} bank${out.successCount === 1 ? "" : "s"}.`
        : `Reloaded ${out.successCount}, ${out.failedCount} failed — see Activity log.`;
    setStatus("plaid-status", summary, out.failedCount === 0 ? "success" : "error");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus("plaid-status", msg, "error");
    log.error("plaid", "Reset-all failed", { error: msg });
  } finally {
    btn.disabled = false;
  }
}

// Silence unused-import warnings for clearPlaidStorage / vault. Both
// stay imported so the module's other consumers can still access them
// without re-importing — clearPlaidStorage is reserved for a future
// "Disconnect all + forget credentials" entry point.
void clearPlaidStorage;

boot();
